import {
  Background, BackgroundVariant, Handle, MiniMap, Panel, Position, ReactFlow, useReactFlow, useViewport,
  type Edge, type Node as FlowNode, type NodeProps, type ReactFlowInstance,
} from "@xyflow/react";
import "@xyflow/react/dist/base.css";
import {
  ArrowDown, ArrowUp, Check, CircleSlash, Clock, Eye, EyeOff, Flag, GitBranch, Hash, Hourglass, ListTodo, Maximize,
  Mail, MessageSquareText, Minus, MoreHorizontal, PencilLine, Plus, RefreshCw, Search, StickyNote, Trash2, UserRound, Users,
  X, Zap,
} from "lucide-react";
import * as React from "react";
import { Link } from "react-router-dom";
import { useTheme } from "@/components/layout/theme";
import { ConfirmDialog } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Callout } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import { Input, Textarea } from "@/components/ui/input";
import { Switch } from "@/components/ui/misc";
import { MultiSelect } from "@/components/ui/multi-select";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FieldControl } from "@/features/contact-fields";
import { draftOf, parseDraft, type Draft } from "@/lib/record-fields";
import { useSlackChannels } from "@/lib/slack";
import { MERGE_FIELDS, fillText, sampleValues } from "@/lib/text-templates";
import type { RecordField } from "@/lib/types";
import { cn, formatDateTime } from "@/lib/utils";
import { layout, type LayoutNode } from "@/lib/workflow-layout";
import {
  CONDITION_FIELDS, LATE_CHOICES, MAX_BRANCHES, MAX_BRANCH_NAME, MAX_CALL_IN_SECONDS, MAX_CONDITIONS, MAX_DEPTH,
  MAX_NOTE_CHARS, MAX_STEPS, MAX_VALUES, MAX_WAIT_SECONDS, MIN_CALL_IN_SECONDS, MIN_WAIT_SECONDS, NONE_PATH,
  fieldTitle, noValue, stageName,
  MAX_DUE_IN_SECONDS, MAX_MIN_TALK_SECONDS, MAX_SLACK_CHARS, MAX_TASK_TITLE, moreStartText, personName,
  slackTextProblem, startStageName, talkText,
  type AssignOwnerStep, type CallAnswered, type CallDirection, type CreateTaskStep, type SendSlackStep,
  type WorkflowEvent,
  allSteps, canAddIf, canMove, findStep, ifReady, ifText, movedBranch, movedStep, valueLabel, newStepId, orList,
  PASSED_WORD, outcomeLabels, pathText, pathsOf, stepAvailable, stepCount, stepLabel, stepsOf, soonNote, toggled, waitText,
  withBranch, withBranchChange, withNoneName, withStart, withStep, withStepChange, withoutBranch, withoutStep,
  type CatalogConditionField, type Condition, type ConditionField, type ConditionOp, type IfStep, type StepList,
  type BoardCounts, type BoardPlace, type CatalogField, type FieldStepValue, type NoteStep, type SetFieldStep,
  type SetStageStep, type WorkflowBody, type WorkflowBranch, type WorkflowCatalog, type WorkflowStep, type WorkflowStepType,
} from "@/lib/workflows";

/**
 * The builder, laid out as GoHighLevel's is, since that is the one the floor knows: a board with
 * the trigger at the top, each action as a box under it joined by a line, a "+" on the line to
 * add an action there, and END at the bottom. A box shows what it does in a few words; clicking it
 * opens its settings in a panel on the right, where every change lands in the draft at once.
 *
 * The board pans and zooms (React Flow): scroll to move, pinch or Ctrl + scroll to zoom, the
 * buttons in the corner to zoom or fit the whole workflow, and a small map once the workflow is big.
 * So many paths, three deep, never push the page wider: the board is as big as the window, and
 * the workflow moves inside it. It opens at full size with the trigger at the top, in the middle.
 * Where things sit is `lib/workflow-layout.ts`'s, a pure function; nothing on the board is
 * dragged.
 *
 * An If/Else splits the line into its named paths, side by side under it, None last, each with its
 * own "+" and its own END: the paths never join again. A path's name on the board opens the
 * If/Else's panel at that path.
 *
 * `problems` are the server's sentences (or the page's own copy of its publish check), keyed by
 * the step they name; `start` is the one about the trigger. Each shows on its own box, in red.
 *
 * `counts` (ACL-407, on a published workflow) put on each box how many leads are on it now and
 * went past it, on each path how many took it, and on the trigger how many started; a count opens
 * Enrollment history at those leads (`onShowLeads`). A box the draft added has none. They are read
 * when the board opens and on Refresh, not pushed.
 */

export interface BuilderProblems {
  start?: string;
  steps: Record<string, string>;
}

type SetBody = (next: (body: WorkflowBody) => WorkflowBody, key?: string) => void;

type Panel =
  | { kind: "trigger" }
  | { kind: "add"; list: StepList; index: number }
  | { kind: "step"; id: string; path?: string }
  | null;

const LANGUAGE_NAMES: Record<string, string> = { en: "English", es: "Spanish" };

const ACTIONS: {
  group: string;
  items: { type: WorkflowStepType; label: string; description: string; icon: typeof Clock }[];
}[] = [
  {
    group: "Communication",
    items: [
      { type: "send_text", label: "Send SMS", description: "Text the lead one of your templates.", icon: MessageSquareText },
      { type: "send_email", label: "Send email", description: "Email the lead one of your email templates.", icon: Mail },
    ],
  },
  {
    group: "Contact",
    items: [
      { type: "set_stage", label: "Update stage", description: "Move the lead to another working or scheduled stage.", icon: Flag },
      { type: "set_field", label: "Update field", description: "Give one of the lead's fields a value.", icon: PencilLine },
      { type: "note", label: "Add note", description: "Put a note on the lead's timeline.", icon: StickyNote },
    ],
  },
  {
    group: "Your team",
    items: [
      { type: "assign_owner", label: "Assign owner", description: "Give the lead to one of your people.", icon: UserRound },
      {
        type: "create_task",
        label: "Create task",
        description: "A to do for one of your people, or the lead's owner, with a due time.",
        icon: ListTodo,
      },
      {
        type: "send_slack",
        label: "Send Slack message",
        description: "Tell your team in a Slack channel, with the lead's details.",
        icon: Hash,
      },
    ],
  },
  {
    group: "Wait",
    items: [{ type: "wait", label: "Wait", description: "Wait a while before the next action.", icon: Clock }],
  },
  {
    group: "Logic",
    items: [
      {
        type: "if",
        label: "If / Else",
        description: "Send leads down different paths, by who they are: by firm, language, state, stage or source.",
        icon: GitBranch,
      },
    ],
  },
];

/** What every box and line on the board needs from the builder. */
interface Canvas {
  body: WorkflowBody;
  catalog: WorkflowCatalog | undefined;
  problems: BuilderProblems;
  readOnly: boolean;
  set: SetBody;
  /** A box brought into view when the board opens: the one a lead is on, from its trail. */
  highlight: string | null;
  /** The step whose panel is open, drawn as chosen. */
  editing: string | null;
  /** The "+" for `index` of a list, or none when nothing can be added. */
  add: (list: StepList, index: number) => (() => void) | undefined;
  open: (id: string, path?: string) => void;
  openTrigger: () => void;
  remove: (step: WorkflowStep) => void;
  /** The leads on each box, when they are shown (ACL-407). */
  counts: BoardCounts | null;
  /** Enrollment history at the leads one count stands for; null for every lead. */
  showLeads?: (place: BoardPlace | null) => void;
}

const CanvasContext = React.createContext<Canvas | null>(null);

function useCanvas(): Canvas {
  const canvas = React.useContext(CanvasContext);
  if (!canvas) throw new Error("a builder node is drawn outside the builder");
  return canvas;
}

export function WorkflowBuilder({
  body, set, catalog, readOnly, problems, highlight = null, counts = null, onShowLeads, onRefreshCounts,
  refreshingCounts = false, countsFailed = false,
}: {
  body: WorkflowBody;
  set: SetBody;
  catalog: WorkflowCatalog | undefined;
  readOnly: boolean;
  problems: BuilderProblems;
  /** Where the published workflow's leads are, box by box; null when it has none to show. */
  counts?: BoardCounts | null;
  onShowLeads?: (place: BoardPlace | null) => void;
  /** Given on a published workflow: the board then has its count buttons, even before the counts
   *  are in or when reading them failed. */
  onRefreshCounts?: () => void;
  refreshingCounts?: boolean;
  countsFailed?: boolean;
  /** A box to draw as chosen and bring into view: the one a lead is on, from its trail (ACL-406). */
  highlight?: string | null;
}) {
  const [panel, setPanel] = React.useState<Panel>(null);
  // An admin who wants the board bare while editing hides the counts; they stay hidden until shown.
  const [countsShown, setCountsShown] = React.useState(true);
  // An If/Else with steps in its paths is deleted only once the admin has said so.
  const [removing, setRemoving] = React.useState<IfStep | null>(null);
  const full = stepCount(body) >= MAX_STEPS;
  // The box a lead's trail asked to see stays outlined until the admin starts working on the board.
  const [shownFor, setShownFor] = React.useState<string | null>(highlight);
  React.useEffect(() => setShownFor(highlight), [highlight]);
  const shown = shownFor && findStep(body, shownFor) ? shownFor : null;
  const open = (next: Panel) => {
    if (readOnly) return;
    setShownFor(null);
    setPanel(next);
  };
  const editing = panel?.kind === "step" ? findStep(body, panel.id) : undefined;
  // A step deleted or undone while its panel is open takes the panel with it.
  React.useEffect(() => {
    if (panel?.kind === "step" && editing === undefined) setPanel(null);
  }, [panel, editing]);

  const remove = (step: WorkflowStep) => {
    setPanel(null);
    if (step.type === "if" && insideOf(step) > 0) setRemoving(step);
    else set((b) => withoutStep(b, step.id));
  };
  const canvas: Canvas = {
    body,
    catalog,
    problems,
    readOnly,
    set,
    editing: panel?.kind === "step" ? panel.id : shown,
    highlight: shown,
    add: (list, index) => (!readOnly && !full ? () => open({ kind: "add", list, index }) : undefined),
    open: (id, path) => open({ kind: "step", id, path }),
    openTrigger: () => open({ kind: "trigger" }),
    remove,
    counts: countsShown ? counts : null,
    showLeads: onShowLeads,
  };
  const inside = removing ? insideOf(removing) : 0;

  return (
    <CanvasContext.Provider value={canvas}>
      <Board
        countTools={
          onRefreshCounts
            ? {
                shown: countsShown,
                toggle: () => setCountsShown((v) => !v),
                refresh: onRefreshCounts,
                refreshing: refreshingCounts,
                at: counts?.countedAt ?? null,
                failed: countsFailed,
              }
            : null
        }
      />

      <Dialog open={panel !== null && (panel.kind !== "step" || editing !== undefined)} onOpenChange={(o) => !o && setPanel(null)}>
        <DialogContent
          variant="drawer"
          // An If/Else's panel puts the focus on the path it was opened for (`PathsFields`), not
          // on the first path's name with its text selected.
          onOpenAutoFocus={(e) => {
            if (editing?.type === "if") e.preventDefault();
          }}
        >
          {panel?.kind === "trigger" && <TriggerPanel body={body} set={set} catalog={catalog} done={() => setPanel(null)} />}
          {panel?.kind === "add" && (
            <AddPanel
              catalog={catalog}
              ifProblem={
                canAddIf(body, panel.list, panel.index)
                  ? null
                  : `Too deep here: an If/Else can sit inside ${MAX_DEPTH - 1} others at most.`
              }
              moving={Math.max(0, stepsOf(body, panel.list).length - panel.index)}
              onPick={(type) => {
                const { list, index } = panel;
                // The id the new action gets, worked out from the same steps `withStep` reads.
                const id = newStepId(body.steps, type);
                set((b) => withStep(b, type, index, list));
                setPanel({ kind: "step", id });
              }}
            />
          )}
          {panel?.kind === "step" && editing && (
            <StepPanel
              step={editing}
              focusPath={panel.path}
              catalog={catalog}
              set={set}
              remove={() => remove(editing)}
              done={() => setPanel(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={removing !== null}
        onOpenChange={(o) => !o && setRemoving(null)}
        title="Delete this If / Else?"
        description={`All its paths go with it: ${inside === 1 ? "1 step" : `${inside} steps`} in all. You can undo it.`}
        confirmLabel="Delete"
        destructive
        onConfirm={() => {
          const id = removing?.id;
          setRemoving(null);
          if (id) set((b) => withoutStep(b, id));
        }}
      />
    </CanvasContext.Provider>
  );
}

/** How many steps sit inside an If/Else's paths. */
function insideOf(step: IfStep): number {
  return allSteps(pathsOf(step).flatMap((p) => p.steps)).length;
}

// --- the board ----------------------------------------------------------------------------------

const NODE_TYPES = {
  trigger: TriggerFlowNode,
  step: StepFlowNode,
  add: AddFlowNode,
  path: PathFlowNode,
  end: EndFlowNode,
};

/** Past this size the small map helps more than it costs. */
const MAP_FROM = 1300;
const MIN_ZOOM = 0.25;
const MAX_ZOOM = 1.5;
/** Room over the trigger, so the workflow does not start against the board's edge. */
const TOP = 56;

/** The board's own buttons for the counts: show or hide them, and read them again. */
interface CountTools {
  shown: boolean;
  toggle: () => void;
  refresh: () => void;
  refreshing: boolean;
  /** When they were counted; null until they are in. */
  at: string | null;
  /** The last read of them failed. */
  failed: boolean;
}

function Board({ countTools }: { countTools: CountTools | null }) {
  const canvas = useCanvas();
  const { resolved } = useTheme();
  const { body, catalog, problems } = canvas;
  const frame = React.useRef<HTMLDivElement>(null);
  const flow = React.useRef<ReactFlowInstance | null>(null);

  const drawn = React.useMemo(
    () => layout(body, { problems: problems.steps, startProblem: Boolean(problems.start), label: (s) => stepLabel(s, catalog) }),
    [body, problems, catalog],
  );
  const nodes: FlowNode[] = React.useMemo(
    () =>
      drawn.nodes.map((n) => ({
        id: n.id,
        type: n.kind,
        position: { x: n.x, y: n.y },
        data: { node: n },
        width: n.width,
        height: n.height,
        // React Flow lets clicks through a node nobody drags or selects; these hold buttons.
        style: { width: n.width, height: n.height, pointerEvents: "all" as const },
        draggable: false,
        selectable: false,
        connectable: false,
        focusable: false,
      })),
    [drawn],
  );
  const edges: Edge[] = React.useMemo(
    () =>
      drawn.edges.map((e) => ({
        id: e.id,
        source: e.source,
        target: e.target,
        type: e.kind === "fork" ? "smoothstep" : "straight",
        pathOptions: e.kind === "fork" ? { borderRadius: 10, offset: 0 } : undefined,
        focusable: false,
        style: { stroke: "var(--color-border-strong)", strokeWidth: 1.5 },
      })),
    [drawn],
  );

  /** The trigger at the top, in the middle: where an admin starts reading. Full size, or as small
   *  as 85% when that shows more of the paths; smaller than that the words get hard to read, and
   *  fitting the whole workflow is one button away. */
  const home = React.useCallback(
    (duration = 200, zoom?: number) => {
      const width = frame.current?.clientWidth ?? 800;
      const z = zoom ?? Math.min(1, Math.max(0.85, (width - 96) / drawn.width));
      flow.current?.setViewport({ x: width / 2, y: TOP, zoom: z }, { duration });
    },
    [drawn.width],
  );

  // "Show on board" from a lead's trail while the board is already up: bring that box into view.
  React.useEffect(() => {
    const target = canvas.highlight ? drawn.nodes.find((n) => n.id === canvas.highlight) : undefined;
    if (target && flow.current) {
      void flow.current.setCenter(target.x + target.width / 2, target.y + target.height / 2, { zoom: 1, duration: 200 });
    }
    // Only a new box to show moves the board, not every redraw of it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canvas.highlight]);

  return (
    <div
      ref={frame}
      className="workflow-board -mx-6 -mb-6 h-[max(560px,calc(100dvh_-_162px))] border-y border-border bg-surface-sunken"
      onFocusCapture={(e) => {
        // A box, a path's name or a "+" reached with Tab is brought into view: the board does not
        // scroll, so one off screen would otherwise take the focus out of sight.
        const instance = flow.current;
        const box = frame.current;
        const id = (e.target as HTMLElement).closest<HTMLElement>(".react-flow__node")?.dataset.id;
        const node = id ? drawn.nodes.find((n) => n.id === id) : undefined;
        if (!instance || !box || !node) return;
        const { x, y, zoom } = instance.getViewport();
        const { width, height } = box.getBoundingClientRect();
        const left = node.x * zoom + x;
        const top = node.y * zoom + y;
        const margin = 24;
        if (left >= margin && top >= margin && left + node.width * zoom <= width - margin && top + node.height * zoom <= height - margin) return;
        void instance.setCenter(node.x + node.width / 2, node.y + node.height / 2, { zoom, duration: 200 });
      }}
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={NODE_TYPES}
        colorMode={resolved}
        onInit={(instance) => {
          flow.current = instance;
          const target = canvas.highlight ? drawn.nodes.find((n) => n.id === canvas.highlight) : undefined;
          if (target) void instance.setCenter(target.x + target.width / 2, target.y + target.height / 2, { zoom: 1, duration: 0 });
          else home(0);
        }}
        nodesDraggable={false}
        // A press on a box is also the start of a pan (the boxes are not dragged), and a slip of a
        // pixel or two would make it a pan and drop the click: a click survives a small slip.
        paneClickDistance={5}
        nodesConnectable={false}
        nodesFocusable={false}
        edgesFocusable={false}
        elementsSelectable={false}
        panOnScroll
        zoomOnScroll={false}
        zoomOnPinch
        zoomOnDoubleClick={false}
        minZoom={MIN_ZOOM}
        maxZoom={MAX_ZOOM}
        proOptions={{ hideAttribution: true }}
      >
        <Background variant={BackgroundVariant.Dots} gap={20} size={1.4} color="var(--color-border-strong)" />
        <BoardTools home={home} countTools={countTools} />
        <ElsewhereNote />
        {(drawn.width > MAP_FROM || drawn.height > MAP_FROM) && (
          <MiniMap
            position="top-right"
            pannable
            zoomable
            ariaLabel="Map of the workflow"
            className="workflow-board-map"
            style={{ width: 168, height: 112 }}
            maskColor="color-mix(in oklch, var(--color-surface-sunken) 70%, transparent)"
            nodeColor={(n) => {
              const kind = (n.data as { node: LayoutNode }).node.kind;
              return kind === "trigger" ? "var(--color-accent-9)" : kind === "step" ? "var(--color-border-strong)" : "transparent";
            }}
            nodeStrokeWidth={0}
          />
        )}
        {/* In a corner, out of the workflow's way; the unsaved-changes bar covers it while it shows. */}
        <Panel position="bottom-left" className="pointer-events-none m-3! hidden rounded-full bg-surface/80 px-2.5 py-1 text-2xs text-text-subtle shadow-xs backdrop-blur sm:block">
          Scroll to move · pinch or Ctrl + scroll to zoom
        </Panel>
      </ReactFlow>
    </div>
  );
}

/** The board's buttons, one small bar at the top left (the bar that says the draft is unsaved sits
 *  over the board's foot): zoom out, the zoom itself, which goes back to full size at the trigger,
 *  zoom in, fit the whole workflow, and back to the trigger. */
function BoardTools({ home, countTools }: { home: (duration?: number, zoom?: number) => void; countTools: CountTools | null }) {
  const { zoomIn, zoomOut, fitView } = useReactFlow();
  const { zoom } = useViewport();
  const tool = cn(
    "grid size-8 place-items-center rounded-lg text-text-muted transition-colors",
    "hover:bg-surface-hover hover:text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-8/40",
    "disabled:pointer-events-none disabled:opacity-40",
  );
  return (
    <Panel position="top-left" className="m-3! flex items-center gap-0.5 rounded-xl border border-border bg-surface/95 p-1 shadow-md backdrop-blur">
      <button type="button" className={tool} onClick={() => zoomOut({ duration: 150 })} disabled={zoom <= MIN_ZOOM + 0.01} title="Zoom out" aria-label="Zoom out">
        <Minus className="size-4" />
      </button>
      <button
        type="button"
        className={cn(tool, "w-auto min-w-12 px-1.5 text-xs font-medium tabular-nums")}
        onClick={() => home(200, 1)}
        title="Full size, back to the trigger"
        aria-label={`Zoom ${Math.round(zoom * 100)}%. Back to full size`}
      >
        {Math.round(zoom * 100)}%
      </button>
      <button type="button" className={tool} onClick={() => zoomIn({ duration: 150 })} disabled={zoom >= MAX_ZOOM - 0.01} title="Zoom in" aria-label="Zoom in">
        <Plus className="size-4" />
      </button>
      <span className="mx-1 h-5 w-px bg-border" aria-hidden />
      <button type="button" className={tool} onClick={() => fitView({ padding: 0.15, maxZoom: 1, duration: 250 })} title="Fit the whole workflow" aria-label="Fit the whole workflow">
        <Maximize className="size-4" />
      </button>
      <button type="button" className={tool} onClick={() => home()} title="Back to the trigger" aria-label="Back to the trigger">
        <Zap className="size-4" />
      </button>
      {countTools && (
        <>
          <span className="mx-1 h-5 w-px bg-border" aria-hidden />
          <button
            type="button"
            className={cn(tool, "flex w-auto gap-1.5 px-2 text-xs font-medium")}
            onClick={countTools.toggle}
            aria-pressed={countTools.shown}
            title={countTools.shown ? "Hide the lead counts" : "Show how many leads are on each box"}
          >
            {countTools.shown ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
            Lead counts
          </button>
          {countTools.shown && (
            <button
              type="button"
              className={cn(tool, countTools.failed && !countTools.refreshing && "text-danger-11")}
              onClick={countTools.refresh}
              disabled={countTools.refreshing}
              title={countRefreshText(countTools)}
              aria-label={countRefreshText(countTools)}
            >
              <RefreshCw className={cn("size-4", countTools.refreshing && "animate-spin")} />
            </button>
          )}
        </>
      )}
    </Panel>
  );
}

function countRefreshText(tools: CountTools): string {
  if (tools.refreshing) return "Counting the leads";
  if (tools.failed) return "Could not count the leads. Try again";
  return tools.at ? `Counted ${formatDateTime(tools.at)}. Count again` : "Count the leads";
}

/** Leads the boxes cannot show, said under the board's buttons so none are lost from the totals:
 *  on a step only an older version has, or on a box this draft took out (it is still the published
 *  version's, so they stay on it until a publish, and finish the version they are on). */
function ElsewhereNote() {
  const { counts, showLeads } = useCanvas();
  if (!counts || (counts.older === 0 && counts.offBoard === 0)) return null;
  return (
    <Panel
      position="top-left"
      className="m-3! mt-[60px]! flex flex-col items-start gap-1 rounded-lg border border-border bg-surface/95 px-2.5 py-1.5 text-xs text-text-muted shadow-sm backdrop-blur"
    >
      {counts.older > 0 && (
        <CountChip
          n={counts.older}
          word={counts.older === 1 ? "lead on an older version" : "leads on older versions"}
          tone="neutral"
          label={`${leads(counts.older)} on a step only an older version has. Show them`}
          onClick={showLeads ? () => showLeads({ at: "older" }) : undefined}
        />
      )}
      {counts.offBoard > 0 && (
        <span title="These boxes are still in the published version. Leads on them finish it there.">
          {leads(counts.offBoard)} waiting on boxes this draft took out
        </span>
      )}
    </Panel>
  );
}

const leads = (n: number) => (n === 1 ? "1 lead" : `${n.toLocaleString()} leads`);

const CHIP_ICON = { waiting: Hourglass, passed: Check, failed: X, start: Users, neutral: null } as const;

/** One count on the board: a small pill with its number and what it counts. A count of leads
 *  opens Enrollment history at them; a zero opens nothing. */
function CountChip({
  n, word, tone, label, onClick,
}: {
  n: number;
  word: string;
  tone: keyof typeof CHIP_ICON;
  /** What it says to a screen reader and on hover. */
  label: string;
  onClick?: () => void;
}) {
  const Icon = CHIP_ICON[tone];
  const chip = cn(
    "inline-flex h-5 shrink-0 items-center gap-1 whitespace-nowrap rounded-full border bg-surface px-1.5 text-[11px] font-semibold tabular-nums shadow-xs",
    n === 0
      ? "border-border text-text-subtle"
      : cn(
          tone === "waiting" && "border-warning-6 text-warning-11",
          tone === "passed" && "border-success-6 text-success-11",
          tone === "failed" && "border-danger-6 text-danger-11",
          tone === "start" && "border-accent-6 text-accent-11",
          tone === "neutral" && "border-border-strong text-text",
        ),
  );
  const inside = (
    <>
      {Icon && <Icon className="size-3" aria-hidden />}
      {n.toLocaleString()} {word}
    </>
  );
  if (!onClick || n === 0) {
    return (
      <span className={chip} title={label}>
        {inside}
      </span>
    );
  }
  return (
    <button
      type="button"
      className={cn(chip, "cursor-pointer transition-colors hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-8/40")}
      title={label}
      aria-label={label}
      onClick={onClick}
    >
      {inside}
    </button>
  );
}

/** A box's counts (ACL-407), on its top edge at the right: the leads on it now and the ones that
 *  went past it, and the ones that failed there when any did. None on a box the draft added. */
function BoxCounts({ step }: { step: WorkflowStep }) {
  const { counts, showLeads } = useCanvas();
  const c = counts?.steps.get(step.id);
  if (!c) return null;
  const show = (at: BoardPlace["at"]) => (showLeads ? () => showLeads({ at, step: step.id }) : undefined);
  const passed = PASSED_WORD[step.type];
  return (
    <div className="pointer-events-none absolute -top-2.5 right-3 z-10 flex gap-1 [&>*]:pointer-events-auto">
      <CountChip n={c.waiting} word="waiting" tone="waiting" label={`${leads(c.waiting)} waiting here now. Show them`} onClick={show("waiting")} />
      <CountChip n={c.passed} word={passed} tone="passed" label={`${leads(c.passed)} ${passed} here. Show them`} onClick={show("passed")} />
      {c.failed > 0 && (
        <CountChip n={c.failed} word="failed" tone="failed" label={`${leads(c.failed)} failed here. Show them`} onClick={show("failed")} />
      )}
    </div>
  );
}

/** The invisible points a line leaves from and arrives at. */
function Ends({ top = true, bottom = true }: { top?: boolean; bottom?: boolean }) {
  return (
    <>
      {top && <Handle type="target" position={Position.Top} isConnectable={false} className="!pointer-events-none !opacity-0" />}
      {bottom && <Handle type="source" position={Position.Bottom} isConnectable={false} className="!pointer-events-none !opacity-0" />}
    </>
  );
}

function TriggerFlowNode() {
  const { body, catalog, problems, readOnly, openTrigger, counts, showLeads } = useCanvas();
  const stats = counts && (
    <div className="pointer-events-none absolute -top-2.5 right-3 z-10 flex [&>*]:pointer-events-auto">
      <CountChip
        n={counts.started}
        word="started"
        tone="start"
        label={`${leads(counts.started)} started this workflow. Show them`}
        onClick={showLeads ? () => showLeads(null) : undefined}
      />
    </div>
  );
  return (
    <>
      <TriggerNode body={body} catalog={catalog} problem={problems.start} readOnly={readOnly} onOpen={openTrigger} stats={stats} />
      <Ends top={false} />
    </>
  );
}

function StepFlowNode({ data }: NodeProps) {
  const node = (data as { node: LayoutNode }).node;
  if (node.kind !== "step") return null;
  return (
    <>
      <StepNode step={node.step} />
      <Ends />
    </>
  );
}

function AddFlowNode({ data }: NodeProps) {
  const canvas = useCanvas();
  const node = (data as { node: LayoutNode }).node;
  if (node.kind !== "add") return null;
  const add = canvas.add(node.list, node.index);
  return (
    <>
      {add ? (
        <button
          type="button"
          onClick={add}
          aria-label={`Add an action after ${node.after}`}
          title="Add an action here"
          className={cn(
            "grid size-7 place-items-center rounded-full border border-border-strong bg-surface text-text-muted shadow-xs transition-all",
            "hover:scale-110 hover:border-accent-8 hover:bg-accent-9 hover:text-white dark:hover:text-gray-1",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-8/40",
          )}
        >
          <Plus className="size-3.5" />
        </button>
      ) : (
        <span className="mx-auto block size-2 translate-y-2.5 rounded-full bg-border-strong" aria-hidden />
      )}
      <Ends />
    </>
  );
}

function PathFlowNode({ data }: NodeProps) {
  const canvas = useCanvas();
  const node = (data as { node: LayoutNode }).node;
  if (node.kind !== "path") return null;
  const step = findStep(canvas.body, node.ifId);
  const branch = step?.type === "if" ? step.branches.find((b) => b.id === node.key) : undefined;
  const hint = node.none ? "Everyone who matches no path above" : branch ? pathText(branch, canvas.catalog) : "";
  const name = node.name.trim() || "A path";
  const pill = cn(
    "mx-auto flex h-8 max-w-full items-center gap-1.5 rounded-full border px-3 text-xs font-semibold shadow-xs transition-colors",
    node.none ? "border-dashed border-border-strong bg-surface text-text-muted" : "border-accent-6 bg-accent-2 text-accent-12",
    !canvas.readOnly && "cursor-pointer hover:border-accent-8",
  );
  const inside = (
    <>
      {node.none ? (
        <CircleSlash className="size-3 shrink-0 text-text-subtle" aria-hidden />
      ) : (
        <span className="grid size-4 shrink-0 place-items-center rounded-full bg-accent-9 text-[10px] font-bold text-white dark:text-gray-1" aria-hidden>
          {node.order + 1}
        </span>
      )}
      <span className="truncate">{name}</span>
    </>
  );
  const went = canvas.counts?.steps.get(node.ifId)?.paths?.find((p) => p.path === node.key)?.went;
  const show = canvas.showLeads;
  return (
    <>
      {/* The pill keeps the middle of the column, where the lines meet it; its count sits on its
          corner. */}
      <div className="relative mx-auto w-fit max-w-full">
        {canvas.readOnly ? (
          <div className={pill} title={hint}>{inside}</div>
        ) : (
          <button
            type="button"
            className={pill}
            title={hint}
            aria-label={`Path "${name}": ${hint}. Open its If / Else`}
            onClick={() => canvas.open(node.ifId, node.key)}
          >
            {inside}
          </button>
        )}
        {went !== undefined && (
          <div className="absolute -right-3 -top-3 z-10">
            <CountChip
              n={went}
              word="went"
              tone="passed"
              label={`${leads(went)} went down "${name}". Show them`}
              onClick={show ? () => show({ at: "passed", step: node.ifId, path: node.key }) : undefined}
            />
          </div>
        )}
      </div>
      <Ends />
    </>
  );
}

function EndFlowNode() {
  return (
    <>
      <span className="mx-auto flex h-[26px] w-[72px] items-center justify-center rounded-full bg-gray-12 text-[11px] font-semibold tracking-wider text-gray-1 dark:bg-gray-11">
        END
      </span>
      <Ends bottom={false} />
    </>
  );
}

function TriggerNode({
  body, catalog, problem, readOnly, onOpen, stats,
}: {
  body: WorkflowBody;
  catalog: WorkflowCatalog | undefined;
  problem?: string;
  readOnly: boolean;
  onOpen: () => void;
  stats?: React.ReactNode;
}) {
  const start = body.starts_when;
  if (start.event === null) {
    return (
      <div className="w-full">
        <button
          type="button"
          onClick={onOpen}
          disabled={readOnly}
          className={cn(
            "group flex h-[76px] w-full items-center gap-3 rounded-xl border-2 border-dashed bg-surface/80 px-3.5 text-left transition-all",
            "hover:-translate-y-px hover:border-accent-8 hover:bg-surface hover:shadow-md",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-8/40",
            problem ? "border-danger-9" : "border-accent-6",
          )}
        >
          <span
            className="grid size-10 shrink-0 place-items-center rounded-xl bg-accent-2 text-accent-11 ring-1 ring-inset ring-accent-5 transition-colors group-hover:bg-accent-9 group-hover:text-white dark:group-hover:text-gray-1"
            aria-hidden
          >
            <Plus className="size-[18px]" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-[10.5px] font-semibold uppercase tracking-wider text-accent-11">Trigger</span>
            <span className="block text-sm font-semibold text-text">Add New Trigger</span>
            <span className="block truncate text-xs text-text-muted">Choose what starts this workflow</span>
          </span>
        </button>
        {problem && (
          <p className="mt-1.5 line-clamp-2 text-center text-xs text-danger-11" role="alert">
            {problem}
          </p>
        )}
      </div>
    );
  }
  const offered = catalog?.starts.find((s) => s.event === start.event);
  const labels = outcomeLabels(catalog);
  const detail =
    start.event === "disposition.set"
      ? orList(start.dispositions.map((slug) => labels.get(slug) ?? "An outcome")) || "No outcome chosen yet"
      : start.event === "lead.created"
        ? orList(start.origins.map((o) => offered?.origins?.find((x) => x.value === o)?.label ?? o)) || "No source chosen yet"
        : start.event === "lead.stage_changed"
          ? (start.to_stages ?? []).length
            ? moreStartText(start, catalog).replace(/^The stage changes: /, "")
            : "No stage chosen yet"
          : start.event === "message.received"
            ? "Any text from the lead but STOP"
            : moreStartText(start, catalog).replace(/^A call ends(: )?/, "") || "Any call with the lead";
  return (
    <Node
      icon={Zap}
      tone="trigger"
      kicker="Trigger"
      title={START_TITLES[start.event]}
      detail={detail}
      problem={problem}
      onOpen={onOpen}
      readOnly={readOnly}
      stats={stats}
    />
  );
}

const START_TITLES: Record<WorkflowEvent, string> = {
  "disposition.set": "Outcome is set",
  "lead.created": "New lead arrives",
  "lead.stage_changed": "Stage changes",
  "message.received": "Lead replies to a text",
  "call.ended": "Call ends",
};

/** What a new action's box says (ACL-408), and whether it is still unfinished. */
function moreActionBox(
  step: AssignOwnerStep | CreateTaskStep | SendSlackStep,
  catalog: WorkflowCatalog | undefined,
): { icon: typeof Clock; detail: string; unfinished: boolean } {
  if (step.type === "assign_owner") {
    const name = personName(step.user_id, catalog);
    return {
      icon: UserRound,
      detail: step.user_id === null ? "Nobody chosen" : (name ?? (catalog ? "Somebody no longer active" : "A person")),
      unfinished: step.user_id === null,
    };
  }
  if (step.type === "create_task") {
    const who = step.assignee === "owner" ? "the lead's owner" : personName(step.user_id, catalog);
    const due = step.due_in_seconds === null ? null : step.due_in_seconds === 0 ? "due at once" : `due in ${waitText(step.due_in_seconds)}`;
    const parts = [step.title?.trim() || "No title yet", who ? `for ${who}` : null, due].filter(Boolean);
    return { icon: ListTodo, detail: parts.join(", "), unfinished: !step.title?.trim() || step.assignee === null || step.due_in_seconds === null };
  }
  return {
    icon: Hash,
    detail: step.channel_id ? `#${step.channel_name ?? step.channel_id}` : "No channel chosen",
    unfinished: !step.channel_id || !step.text?.trim(),
  };
}

function StepNode({ step }: { step: WorkflowStep }) {
  const canvas = useCanvas();
  const { body, catalog, readOnly, set } = canvas;
  const problem = canvas.problems.steps[step.id];
  const onOpen = () => canvas.open(step.id);
  const chosen = canvas.editing === step.id;
  const label = stepLabel(step, catalog);
  const stats = <BoxCounts step={step} />;
  const menu = !readOnly && (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon-sm" aria-label={`${label}: options`}>
          <MoreHorizontal />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {step.type !== "if" && (
          <>
            <DropdownMenuItem disabled={!canMove(body, step.id, -1)} onSelect={() => set((b) => movedStep(b, step.id, -1))}>
              <ArrowUp />
              Move up
            </DropdownMenuItem>
            <DropdownMenuItem disabled={!canMove(body, step.id, 1)} onSelect={() => set((b) => movedStep(b, step.id, 1))}>
              <ArrowDown />
              Move down
            </DropdownMenuItem>
          </>
        )}
        <DropdownMenuItem destructive onSelect={() => canvas.remove(step)}>
          <Trash2 />
          {step.type === "if" ? "Delete If / Else" : "Delete action"}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
  if (step.type === "wait") {
    return (
      <Node
        icon={Clock}
        tone="wait"
        title="Wait"
        detail={waitText(step.seconds)}
        problem={problem}
        onOpen={onOpen}
        readOnly={readOnly}
        menu={menu}
        chosen={chosen}
        stats={stats}
      />
    );
  }
  if (step.type === "if") {
    const ready = ifReady(step);
    const count = pathsOf(step).length;
    return (
      <Node
        icon={GitBranch}
        tone="logic"
        title="If / Else"
        badge={`${count} paths`}
        detail={ready ? ifText(step) : "Paths not finished"}
        muted={!ready}
        problem={problem}
        onOpen={onOpen}
        readOnly={readOnly}
        menu={menu}
        chosen={chosen}
        stats={stats}
      />
    );
  }
  if (step.type === "set_stage" || step.type === "set_field" || step.type === "note") {
    const detail =
      step.type === "set_stage"
        ? step.stage
          ? stageName(step.stage, catalog) + (step.call_in_seconds !== null ? `, called in ${waitText(step.call_in_seconds)}` : "")
          : "No stage chosen"
        : step.type === "set_field"
          ? step.field
            ? fieldTitle(step.field, catalog)
            : "No field chosen"
          : step.text?.trim() || "No note written";
    const unfinished =
      step.type === "set_stage" ? !step.stage : step.type === "set_field" ? !step.field || noValue(step.value) : !step.text?.trim();
    const icon = step.type === "set_stage" ? Flag : step.type === "set_field" ? PencilLine : StickyNote;
    return (
      <Node
        icon={icon}
        tone="action"
        title={PANEL_TEXT[step.type].title}
        detail={detail}
        muted={unfinished}
        problem={problem}
        onOpen={onOpen}
        readOnly={readOnly}
        menu={menu}
        chosen={chosen}
        stats={stats}
      />
    );
  }
  if (step.type === "assign_owner" || step.type === "create_task" || step.type === "send_slack") {
    const box = moreActionBox(step, catalog);
    return (
      <Node
        icon={box.icon}
        tone="action"
        title={PANEL_TEXT[step.type].title}
        detail={box.detail}
        muted={box.unfinished}
        problem={problem}
        onOpen={onOpen}
        readOnly={readOnly}
        menu={menu}
        chosen={chosen}
      />
    );
  }
  const email = step.type === "send_email";
  const template = catalog?.templates.find((t) => t.slug === step.template);
  const available = stepAvailable(catalog, step.type);
  return (
    <Node
      icon={email ? Mail : MessageSquareText}
      tone="action"
      title={email ? "Send email" : "Send SMS"}
      detail={
        !available
          ? "Coming soon"
          : template
            ? `${template.name}${template.active === false ? " (turned off)" : ""}`
            : "No template chosen"
      }
      muted={!available || !template || template.active === false}
      problem={problem}
      onOpen={onOpen}
      readOnly={readOnly}
      menu={menu}
      chosen={chosen}
      stats={stats}
    />
  );
}

function Node({
  icon: Icon, tone, kicker, title, badge, detail, muted = false, problem, onOpen, readOnly, menu, chosen = false, stats,
}: {
  icon: React.ComponentType<{ className?: string }>;
  tone: "trigger" | "action" | "wait" | "logic";
  kicker?: string;
  title: string;
  /** A short tag beside the title: "3 paths". */
  badge?: string;
  detail: string;
  muted?: boolean;
  problem?: string;
  onOpen: () => void;
  readOnly: boolean;
  menu?: React.ReactNode;
  /** Its panel is open. */
  chosen?: boolean;
  /** Its lead counts, on its top edge (ACL-407). */
  stats?: React.ReactNode;
}) {
  const body = (
    <>
      <span
        className={cn(
          "grid size-10 shrink-0 place-items-center rounded-xl",
          tone === "trigger" && "bg-accent-9 text-white shadow-sm dark:text-gray-1",
          tone === "action" && "bg-success-2 text-success-11 ring-1 ring-inset ring-success-4",
          tone === "wait" && "bg-warning-2 text-warning-11 ring-1 ring-inset ring-warning-4",
          tone === "logic" && "bg-accent-2 text-accent-11 ring-1 ring-inset ring-accent-4",
        )}
        aria-hidden
      >
        <Icon className="size-[18px]" />
      </span>
      <span className="min-w-0 flex-1">
        {kicker && <span className="block text-[10.5px] font-semibold uppercase tracking-wider text-accent-11">{kicker}</span>}
        <span className="flex items-center gap-2">
          <span className="truncate text-sm font-semibold text-text">{title}</span>
          {badge && (
            <span className="shrink-0 rounded-full bg-surface-sunken px-1.5 py-px text-[10.5px] font-medium text-text-muted ring-1 ring-inset ring-border">
              {badge}
            </span>
          )}
        </span>
        <span className={cn("block truncate text-xs", muted ? "text-text-subtle" : "text-text-muted")} title={detail}>
          {detail}
        </span>
      </span>
    </>
  );
  const box = cn(
    "flex h-[76px] w-full items-center gap-3 rounded-xl border bg-surface px-3.5 text-left shadow-sm transition-all",
    menu && "pr-12",
    problem ? "border-danger-9" : chosen ? "border-accent-8 ring-2 ring-accent-8/25" : "border-border",
  );
  return (
    <div className="w-full">
      {/* The menu is beside the box, not in it: a click in the menu (drawn in a portal) would
          otherwise still reach the box through React and open its panel too. */}
      <div className="relative">
        {readOnly ? (
          <div className={box}>{body}</div>
        ) : (
          <button
            type="button"
            onClick={onOpen}
            aria-label={`${kicker ? `${kicker}: ` : ""}${title}, ${detail}. Open its settings`}
            className={cn(
              box,
              "cursor-pointer hover:-translate-y-px hover:border-accent-8 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-8/40",
            )}
          >
            {body}
          </button>
        )}
        {menu && <div className="absolute right-2 top-1/2 -translate-y-1/2">{menu}</div>}
        {stats}
      </div>
      {problem && (
        <p className="mt-1.5 line-clamp-2 px-2 text-center text-xs leading-4 text-danger-11" role="alert">
          {problem}
        </p>
      )}
    </div>
  );
}

// --- the panels on the right -----------------------------------------------------------------

function TriggerPanel({
  body, set, catalog, done,
}: {
  body: WorkflowBody;
  set: SetBody;
  catalog: WorkflowCatalog | undefined;
  done: () => void;
}) {
  const start = body.starts_when;
  const outcome = catalog?.starts.find((s) => s.event === "disposition.set");
  const newLead = catalog?.starts.find((s) => s.event === "lead.created");
  return (
    <>
      <DialogHeader>
        <DialogTitle>Workflow trigger</DialogTitle>
        <DialogDescription>Choose what starts this workflow for a lead.</DialogDescription>
      </DialogHeader>
      <DialogBody className="space-y-5">
        <div
          className="space-y-2"
          role="radiogroup"
          aria-label="Trigger"
          onKeyDown={(e) => {
            // Arrow keys move between the choices a radio group offers, as a native one does.
            if (!["ArrowDown", "ArrowUp", "ArrowLeft", "ArrowRight"].includes(e.key)) return;
            const choices = [...e.currentTarget.querySelectorAll<HTMLButtonElement>('[role="radio"]:not(:disabled)')];
            const at = choices.indexOf(document.activeElement as HTMLButtonElement);
            const next = choices[(at + (e.key === "ArrowDown" || e.key === "ArrowRight" ? 1 : -1) + choices.length) % choices.length];
            if (next) {
              e.preventDefault();
              next.focus();
              next.click();
            }
          }}
        >
          <Choice
            selected={start.event === "disposition.set"}
            title="Outcome is set"
            description="A rep, or the dialer, sets an outcome on a lead, such as No answer."
            onSelect={() => set((b) => withStart(b, "disposition.set"))}
          />
          <Choice
            selected={start.event === "lead.created"}
            title="New lead arrives"
            description={newLead?.available === false ? soonNote(newLead.note) : "From Intake, GHL or Zapier."}
            disabled={newLead?.available === false}
            soon={newLead?.available === false}
            onSelect={() => set((b) => withStart(b, "lead.created"))}
          />
          {MORE_STARTS.map(({ event, title, description }) => {
            const offered = catalog?.starts.find((s) => s.event === event);
            // A core-api that does not list it would refuse it on save: it is not offered.
            if (catalog && !offered) return null;
            const soon = offered?.available === false;
            return (
              <Choice
                key={event}
                selected={start.event === event}
                title={title}
                description={soon ? soonNote(offered?.note) : description}
                disabled={soon}
                soon={soon}
                onSelect={() => set((b) => withStart(b, event))}
              />
            );
          })}
        </div>

        {start.event === "lead.stage_changed" && <StageStartFields body={body} catalog={catalog} set={set} />}

        {start.event === "message.received" && (
          <Callout tone="info">
            Any text from the lead starts it, except STOP and the other opt out words, which never do. The text that starts
            it does not stop it, even with "Stop when the lead replies" on; a later reply does.
          </Callout>
        )}

        {start.event === "call.ended" && <CallStartFields body={body} catalog={catalog} set={set} />}

        {start.event === "disposition.set" && (
          <div className="space-y-1.5">
            <label htmlFor="workflow-outcomes" className="text-xs font-medium text-text">
              Which outcomes
            </label>
            <OutcomePicker
              id="workflow-outcomes"
              outcomes={outcome?.dispositions ?? []}
              value={start.dispositions}
              onChange={(dispositions) => set((b) => ({ ...b, starts_when: { ...b.starts_when, dispositions } }))}
            />
          </div>
        )}

        {start.event === "lead.created" && (
          <fieldset className="space-y-1.5">
            <legend className="text-xs font-medium text-text">Where the new leads come from</legend>
            <div className="space-y-1.5">
              {(newLead?.origins ?? []).map((o) => (
                <label key={o.value} className="flex items-center gap-2 text-sm text-text">
                  <input
                    type="checkbox"
                    className="size-3.5 accent-accent-9"
                    checked={start.origins.includes(o.value)}
                    onChange={() =>
                      set((b) => ({ ...b, starts_when: { ...b.starts_when, origins: toggled(b.starts_when.origins, o.value) } }))
                    }
                  />
                  {o.label}
                </label>
              ))}
            </div>
            <p className="text-xs text-text-muted">
              A CSV import or a contact a rep types in is left out unless you tick it, so old contacts are never texted by
              mistake.
            </p>
          </fieldset>
        )}
      </DialogBody>
      <DialogFooter>
        <Button onClick={done}>Done</Button>
      </DialogFooter>
    </>
  );
}

const MORE_STARTS: { event: WorkflowEvent; title: string; description: string }[] = [
  {
    event: "lead.stage_changed",
    title: "Stage changes",
    description: "A lead moves to a stage you choose: by hand, by an outcome or by another workflow.",
  },
  { event: "message.received", title: "Lead replies to a text", description: "A text comes in from the lead." },
  { event: "call.ended", title: "Call ends", description: "A call with the lead is over: one the dialer or a rep made." },
];

type StartsWhen = WorkflowBody["starts_when"];

function withStarts(body: WorkflowBody, change: Partial<StartsWhen>): WorkflowBody {
  return { ...body, starts_when: { ...body.starts_when, ...change } };
}

/**
 * "Stage changes" (ACL-408): the stages a lead moves to, and, if the admin wants, the ones it moves
 * from. Any of the tenant's stages, end stages too: "moved to Signed" is a fine reason to tell the
 * team. A stage the draft names that the tenant no longer has stays named with a Remove.
 */
function StageStartFields({ body, catalog, set }: { body: WorkflowBody; catalog: WorkflowCatalog | undefined; set: SetBody }) {
  const offered = catalog?.starts.find((s) => s.event === "lead.stage_changed");
  const stages = offered?.stages ?? [];
  const known = new Set(stages.map((s) => s.slug));
  const to = body.starts_when.to_stages ?? [];
  const from = body.starts_when.from_stages ?? [];
  const options = stages.map((s) => ({
    value: s.slug,
    label: s.label,
    hint: s.group === "exit" ? "end stage" : s.group === "scheduled" ? "scheduled" : undefined,
  }));
  const gone = catalog ? [...from, ...to].filter((slug) => !known.has(slug)) : [];
  return (
    <div className="space-y-4">
      {[...new Set(gone)].map((slug) => (
        <div
          key={slug}
          className="flex items-center justify-between gap-2 rounded-md border border-warning-4 bg-warning-2 px-3 py-1.5 text-xs"
        >
          <span>
            <span className="font-medium text-text">"{slug}"</span>{" "}
            <span className="text-warning-11">is no longer one of your stages.</span>
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              set((b) =>
                withStarts(b, {
                  from_stages: (b.starts_when.from_stages ?? []).filter((v) => v !== slug),
                  to_stages: (b.starts_when.to_stages ?? []).filter((v) => v !== slug),
                }),
              )
            }
          >
            Remove
          </Button>
        </div>
      ))}
      <div className="space-y-1.5">
        <p className="text-xs font-medium text-text">Moves to</p>
        <MultiSelect
          label="Moves to"
          placeholder="Choose…"
          className="h-9 w-full max-w-none justify-start"
          options={options}
          value={to.filter((v) => known.has(v) || !catalog)}
          onChange={(next) => set((b) => withStarts(b, { to_stages: [...to.filter((v) => catalog && !known.has(v)), ...next] }))}
          emptyText="No stages yet"
          noneText="None picked yet: pick at least one"
        />
        <p className="text-xs text-text-muted">Any one of them starts the workflow.</p>
      </div>
      <div className="space-y-1.5">
        <p className="text-xs font-medium text-text">From</p>
        <MultiSelect
          label="From"
          placeholder="Any stage"
          className="h-9 w-full max-w-none justify-start"
          options={options}
          value={from.filter((v) => known.has(v) || !catalog)}
          onChange={(next) => set((b) => withStarts(b, { from_stages: [...from.filter((v) => catalog && !known.has(v)), ...next] }))}
          emptyText="No stages yet"
          noneText="None picked: from any stage"
        />
      </div>
      <Callout tone="info">
        A new lead's first stage does not count, nor does a move an import makes. A move this workflow makes itself never
        starts it again.
      </Callout>
    </div>
  );
}

/**
 * "Call ends" (ACL-408): which calls (a call the lead makes says nothing when it ends yet, so it is
 * offered as coming soon), whether a rep talked on it, and with a rep, for how long at least.
 */
function CallStartFields({ body, catalog, set }: { body: WorkflowBody; catalog: WorkflowCatalog | undefined; set: SetBody }) {
  const offered = catalog?.starts.find((s) => s.event === "call.ended");
  const start = body.starts_when;
  const directions = start.call_directions ?? ["outbound"];
  const answered = start.call_answered ?? "any";
  const least = start.min_talk_seconds ?? 0;
  const choices = offered?.call_directions ?? [{ value: "outbound" as CallDirection, label: "Calls we make", available: true }];
  const answers = offered?.call_answered ?? [
    { value: "any" as CallAnswered, label: "Any call" },
    { value: "yes" as CallAnswered, label: "A rep talked with the lead" },
    { value: "no" as CallAnswered, label: "Nobody talked with the lead" },
  ];
  const [minutes, setMinutes] = React.useState(() => (least ? String(least / 60) : ""));
  React.useEffect(() => {
    // An undo, or another answer, changes the time from outside the box.
    if (Math.round(Number(minutes || 0) * 60) !== least) setMinutes(least ? String(least / 60) : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [least]);
  const typed = Number(minutes || 0);
  const problem =
    !Number.isFinite(typed) || typed < 0
      ? "Type a number of minutes."
      : !Number.isInteger(typed)
        ? "A whole number of minutes, please."
        : typed * 60 > MAX_MIN_TALK_SECONDS
          ? `At most ${MAX_MIN_TALK_SECONDS / 3600} hours.`
          : null;
  return (
    <div className="space-y-4">
      <fieldset className="space-y-1.5">
        <legend className="text-xs font-medium text-text">Which calls</legend>
        {choices.map((c) => (
          <label key={c.value} className={cn("flex items-center gap-2 text-sm text-text", c.available === false && "text-text-muted")}>
            <input
              type="checkbox"
              className="size-3.5 accent-accent-9"
              disabled={c.available === false && !directions.includes(c.value)}
              checked={directions.includes(c.value)}
              onChange={() => set((b) => withStarts(b, { call_directions: toggled(b.starts_when.call_directions ?? ["outbound"], c.value) }))}
            />
            {c.label}
            {c.available === false && <SoonTag />}
          </label>
        ))}
        {choices.some((c) => c.available === false) && (
          <p className="text-xs text-text-muted">{soonNote(choices.find((c) => c.available === false)?.note)}</p>
        )}
      </fieldset>
      <Field label="Talked with a rep" htmlFor="workflow-call-answered">
        <Select
          value={answered}
          onValueChange={(v) =>
            set((b) => withStarts(b, { call_answered: v as CallAnswered, min_talk_seconds: v === "yes" ? (b.starts_when.min_talk_seconds ?? 0) : 0 }))
          }
        >
          <SelectTrigger id="workflow-call-answered">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {answers.map((a) => (
              <SelectItem key={a.value} value={a.value}>
                {a.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
      {answered === "yes" && (
        <div className="space-y-1.5">
          <label htmlFor="workflow-min-talk" className="block text-xs font-medium text-text">
            For at least (minutes)
          </label>
          <Input
            id="workflow-min-talk"
            type="number"
            min={0}
            step={1}
            inputMode="numeric"
            className="w-28"
            placeholder="0"
            invalid={Boolean(problem)}
            aria-describedby={problem ? "workflow-min-talk-error" : undefined}
            value={minutes}
            onChange={(e) => {
              setMinutes(e.target.value);
              const n = Number(e.target.value || 0);
              if (Number.isInteger(n) && n >= 0 && n * 60 <= MAX_MIN_TALK_SECONDS) {
                set((b) => withStarts(b, { min_talk_seconds: n * 60 }), "min-talk");
              }
            }}
          />
          {problem ? (
            <p id="workflow-min-talk-error" role="alert" className="text-xs text-danger-11">
              {problem}
            </p>
          ) : (
            <p className="text-xs text-text-muted">{least ? `Only a call of ${talkText(least)} or more.` : "0 or empty: any length."}</p>
          )}
        </div>
      )}
      <Callout tone="info">
        A call that ends never stops a lead's other workflows: the outcome the rep sets does that.
      </Callout>
    </div>
  );
}

/**
 * The outcomes a workflow starts on, as a list to tick inside the panel, with a search once the
 * catalog is long. An outcome the tenant has since retired stays named in a box of its own with a
 * Remove, so a draft that names one can be put right: it cannot be published until it is gone.
 */
function OutcomePicker({
  id, outcomes, value, onChange,
}: {
  id: string;
  outcomes: { slug: string; label: string; retired?: boolean }[];
  value: string[];
  onChange: (next: string[]) => void;
}) {
  const [query, setQuery] = React.useState("");
  const active = outcomes.filter((o) => !o.retired);
  const activeSlugs = new Set(active.map((o) => o.slug));
  const gone = value
    .filter((slug) => !activeSlugs.has(slug))
    .map((slug) => ({ slug, label: outcomes.find((o) => o.slug === slug)?.label ?? "An outcome you no longer have" }));
  const needle = query.trim().toLowerCase();
  const shown = needle ? active.filter((o) => o.label.toLowerCase().includes(needle)) : active;
  const picked = value.filter((slug) => activeSlugs.has(slug)).length;
  return (
    <div className="space-y-2">
      {gone.map((o) => (
        <div
          key={o.slug}
          className="flex items-center justify-between gap-2 rounded-md border border-warning-4 bg-warning-2 px-3 py-1.5 text-xs"
        >
          <span>
            <span className="font-medium text-text">{o.label}</span>{" "}
            <span className="text-warning-11">is no longer one of your outcomes.</span>
          </span>
          <Button variant="ghost" size="sm" onClick={() => onChange(value.filter((v) => v !== o.slug))}>
            Remove
          </Button>
        </div>
      ))}
      {active.length > 8 && (
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-text-subtle" aria-hidden />
          <Input
            id={id}
            placeholder="Search outcomes"
            aria-label="Search outcomes"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-8"
          />
        </div>
      )}
      <div
        className="max-h-72 divide-y divide-border overflow-y-auto rounded-md border border-border"
        role="group"
        aria-label="Outcomes"
        id={active.length > 8 ? undefined : id}
        tabIndex={active.length > 8 ? undefined : -1}
      >
        {shown.length === 0 ? (
          <p className="px-3 py-2 text-xs text-text-muted">
            {active.length === 0 ? "There are no outcomes in your catalog yet." : "No outcome matches that."}
          </p>
        ) : (
          shown.map((o) => (
            <label key={o.slug} className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm text-text hover:bg-surface-hover">
              <input
                type="checkbox"
                className="size-3.5 accent-accent-9"
                checked={value.includes(o.slug)}
                onChange={() => onChange(toggled(value, o.slug))}
              />
              {o.label}
            </label>
          ))
        )}
      </div>
      <p className="text-xs text-text-muted">
        {picked === 0 ? "Pick at least one." : `${picked} picked.`} Any one of them starts the workflow.
      </p>
    </div>
  );
}

/** The actions the server offers, once its catalog has loaded: a console newer than its core-api
 *  (an If/Else before the server knows one) never offers a step that would be refused on save. */
function offeredActions(catalog: WorkflowCatalog | undefined): typeof ACTIONS {
  if (!catalog) return ACTIONS;
  const known = new Set(catalog.step_types.map((t) => t.type));
  return ACTIONS.map((g) => ({ ...g, items: g.items.filter((a) => known.has(a.type)) })).filter((g) => g.items.length > 0);
}

function AddPanel({
  catalog, ifProblem, moving, onPick,
}: {
  catalog: WorkflowCatalog | undefined;
  /** Why an If/Else cannot go here, or null when it can. */
  ifProblem: string | null;
  /** How many steps are under this "+": an If/Else added here takes them into its Yes path. */
  moving: number;
  onPick: (type: WorkflowStepType) => void;
}) {
  return (
    <>
      <DialogHeader>
        <DialogTitle>Add an action</DialogTitle>
        <DialogDescription>Pick what happens next for the lead.</DialogDescription>
      </DialogHeader>
      <DialogBody className="space-y-5">
        {offeredActions(catalog).map((group) => (
          <div key={group.group} className="space-y-1.5">
            <p className="text-xs font-semibold uppercase tracking-wide text-text-subtle">{group.group}</p>
            {group.items.map((a) => {
              const offered = catalog?.step_types.find((t) => t.type === a.type);
              const available = stepAvailable(catalog, a.type) && !(a.type === "if" && ifProblem);
              const description =
                a.type === "if" && ifProblem
                  ? ifProblem
                  : a.type === "if" && moving > 0
                    ? `${a.description} The ${moving === 1 ? "step" : `${moving} steps`} under here move into its Yes path.`
                    : available
                      ? a.description
                      : soonNote(offered?.note);
              return (
                <button
                  key={a.type}
                  type="button"
                  disabled={!available}
                  onClick={() => onPick(a.type)}
                  className={cn(
                    "flex w-full items-center gap-3 rounded-lg border border-border bg-surface px-3 py-2.5 text-left transition-colors",
                    "hover:border-accent-8 hover:bg-accent-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-8/40",
                    "disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:border-border disabled:hover:bg-surface",
                  )}
                >
                  <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-surface-sunken text-text" aria-hidden>
                    <a.icon className="size-4" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium text-text">{a.label}</span>
                    <span className="block text-xs text-text-muted">{description}</span>
                  </span>
                  {!available && !(a.type === "if" && ifProblem) && <SoonTag />}
                </button>
              );
            })}
          </div>
        ))}
      </DialogBody>
    </>
  );
}

const UNITS = [
  { value: "minutes", seconds: 60, label: "Minutes" },
  { value: "hours", seconds: 3600, label: "Hours" },
  { value: "days", seconds: 86400, label: "Days" },
] as const;
type Unit = (typeof UNITS)[number]["value"];

const unitOf = (seconds: number): Unit =>
  seconds % 86400 === 0 ? "days" : seconds % 3600 === 0 ? "hours" : "minutes";

const QUICK_WAITS = [60, 5 * 60, 15 * 60, 3600, 86400, 2 * 86400, 7 * 86400];

const PANEL_TEXT: Record<WorkflowStepType, { title: string; description: string }> = {
  wait: { title: "Wait", description: "How long to wait before the next action." },
  send_text: { title: "Send SMS", description: "Which text the lead gets." },
  send_email: { title: "Send email", description: "Which email the lead gets." },
  if: {
    title: "If / Else",
    description: "Each lead goes down the first path, from the top, whose conditions all match. Everyone else takes the last one.",
  },
  set_stage: { title: "Update stage", description: "Which stage the lead moves to." },
  set_field: { title: "Update field", description: "Which field of the lead changes, and to what." },
  note: { title: "Add note", description: "What the note on the lead's timeline says." },
  assign_owner: { title: "Assign owner", description: "Who the lead belongs to from this step on." },
  create_task: { title: "Create task", description: "A to do about the lead, for one of your people." },
  send_slack: { title: "Send Slack message", description: "What your team reads in Slack, and where." },
};

function StepPanel({
  step, focusPath, catalog, set, remove, done,
}: {
  step: WorkflowStep;
  /** For an If/Else opened from one of its paths on the board: that path. */
  focusPath?: string;
  catalog: WorkflowCatalog | undefined;
  set: SetBody;
  remove: () => void;
  done: () => void;
}) {
  return (
    <>
      <DialogHeader>
        <DialogTitle>{PANEL_TEXT[step.type].title}</DialogTitle>
        <DialogDescription>{PANEL_TEXT[step.type].description}</DialogDescription>
      </DialogHeader>
      <DialogBody className="space-y-5">
        {step.type === "wait" && <WaitFields step={step} set={set} />}
        {step.type === "send_text" && <SmsFields step={step} catalog={catalog} set={set} />}
        {step.type === "send_email" && <EmailFields step={step} catalog={catalog} set={set} />}
        {step.type === "if" && <PathsFields step={step} focusPath={focusPath} catalog={catalog} set={set} />}
        {step.type === "set_stage" && <StageFields step={step} catalog={catalog} set={set} />}
        {step.type === "set_field" && <FieldFields step={step} catalog={catalog} set={set} />}
        {step.type === "note" && <NoteFields step={step} set={set} />}
        {step.type === "assign_owner" && <OwnerFields step={step} catalog={catalog} set={set} />}
        {step.type === "create_task" && <TaskFields step={step} catalog={catalog} set={set} />}
        {step.type === "send_slack" && <SlackFields step={step} catalog={catalog} set={set} />}
      </DialogBody>
      <DialogFooter className="justify-between">
        <Button variant="ghost" className="text-danger-11" onClick={remove}>
          <Trash2 />
          {step.type === "if" ? "Delete If / Else" : "Delete action"}
        </Button>
        <Button onClick={done}>Done</Button>
      </DialogFooter>
    </>
  );
}

/**
 * An If/Else's paths, as GoHighLevel's: a card each, read top to bottom, with its name, its
 * conditions (all must match) and the arrows that change the order, and None last, which only
 * takes a name. A path with steps on it is deleted only once the admin has said so.
 */
function PathsFields({
  step, focusPath, catalog, set,
}: {
  step: IfStep;
  focusPath?: string;
  catalog: WorkflowCatalog | undefined;
  set: SetBody;
}) {
  const fields = catalog?.condition_fields ?? [];
  const [deleting, setDeleting] = React.useState<WorkflowBranch | null>(null);
  const cards = React.useRef<Map<string, HTMLElement>>(new Map());
  const [lit, setLit] = React.useState<string | null>(focusPath ?? null);

  // Opened from a path's name on the board: bring that path into view, light it for a moment, and
  // put the focus there; opened from the box, the focus starts at the first path.
  React.useEffect(() => {
    const target = cards.current.get(focusPath ?? step.branches[0]?.id ?? NONE_PATH);
    target?.focus({ preventScroll: true });
    if (!focusPath) return;
    target?.scrollIntoView({ block: "center", behavior: "smooth" });
    setLit(focusPath);
    const off = window.setTimeout(() => setLit(null), 1600);
    return () => window.clearTimeout(off);
    // Only where the panel was opened, not every edit, moves the focus.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusPath]);

  const names = [...step.branches.map((b) => b.name), step.none.name].map((n) => n.trim().toLowerCase());
  const nameProblem = (name: string): string | null => {
    const clean = name.trim();
    if (!clean) return "A path needs a name.";
    return names.filter((n) => n === clean.toLowerCase()).length > 1 ? `Two paths are called "${clean}".` : null;
  };
  const keep = (key: string) => (el: HTMLElement | null) => {
    if (el) cards.current.set(key, el);
    else cards.current.delete(key);
  };

  return (
    <>
      <ol className="space-y-3" aria-label="Paths">
        {step.branches.map((branch, i) => (
          <li
            key={branch.id}
            ref={keep(branch.id)}
            tabIndex={-1}
            aria-label={`Path ${i + 1}: ${branch.name}`}
            className={cn(
              "rounded-xl border bg-surface shadow-xs transition-shadow focus-visible:outline-none",
              lit === branch.id ? "border-accent-8 ring-2 ring-accent-8/30" : "border-border",
            )}
          >
            <PathCard
              branch={branch}
              order={i}
              count={step.branches.length}
              nameProblem={nameProblem(branch.name)}
              fields={fields}
              catalog={catalog}
              onName={(name) => set((b) => withBranchChange(b, step.id, branch.id, { name }), `path-name-${branch.id}`)}
              onConditions={(conditions) => set((b) => withBranchChange(b, step.id, branch.id, { conditions }))}
              onMove={(by) => set((b) => movedBranch(b, step.id, branch.id, by))}
              onDelete={() =>
                allSteps(branch.steps).length > 0 ? setDeleting(branch) : set((b) => withoutBranch(b, step.id, branch.id))
              }
            />
          </li>
        ))}
        {step.branches.length === 0 && (
          <li className="rounded-xl border border-dashed border-border-strong px-4 py-5 text-center text-sm text-text-muted">
            No path yet. Add one: an If/Else needs at least one to be published.
          </li>
        )}
      </ol>

      <button
        type="button"
        disabled={step.branches.length >= MAX_BRANCHES}
        onClick={() => set((b) => withBranch(b, step.id))}
        className={cn(
          "flex w-full items-center justify-center gap-1.5 rounded-xl border border-dashed border-accent-7 px-3 py-2.5 text-sm font-medium text-accent-11 transition-colors",
          "hover:border-accent-8 hover:bg-accent-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-8/40",
          "disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent",
        )}
      >
        <Plus className="size-4" aria-hidden />
        {step.branches.length >= MAX_BRANCHES ? `At most ${MAX_BRANCHES} paths` : "Add path"}
      </button>

      <div
        ref={keep(NONE_PATH)}
        tabIndex={-1}
        aria-label={`Last path: ${step.none.name}`}
        className={cn(
          "rounded-xl border border-dashed bg-surface-sunken/60 p-3 transition-shadow focus-visible:outline-none",
          lit === NONE_PATH ? "border-accent-8 ring-2 ring-accent-8/30" : "border-border-strong",
        )}
      >
        <div className="flex items-center gap-2.5">
          <span className="grid size-6 shrink-0 place-items-center rounded-full bg-gray-4 text-text-muted" aria-hidden>
            <CircleSlash className="size-3.5" />
          </span>
          <Input
            aria-label="Name of the last path"
            className="h-9 flex-1 bg-surface text-sm font-medium"
            maxLength={MAX_BRANCH_NAME}
            value={step.none.name}
            invalid={Boolean(nameProblem(step.none.name))}
            onChange={(e) => set((b) => withNoneName(b, step.id, e.target.value), `path-name-${NONE_PATH}`)}
          />
        </div>
        {nameProblem(step.none.name) ? (
          <p className="mt-1.5 pl-[34px] text-xs text-danger-11" role="alert">
            {nameProblem(step.none.name)}
          </p>
        ) : (
          <p className="mt-1.5 pl-[34px] text-xs text-text-muted">Everyone who matches no path above. Always last.</p>
        )}
      </div>

      <Callout tone="info">
        A lead's details are checked when they reach this step, not when they entered the workflow. The paths never join
        again: a lead whose path has no steps finishes the workflow there.
      </Callout>

      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(o) => !o && setDeleting(null)}
        title={`Delete the path "${deleting?.name.trim() || "this path"}"?`}
        description={(() => {
          const n = deleting ? allSteps(deleting.steps).length : 0;
          return `The ${n === 1 ? "step" : `${n} steps`} on it go${n === 1 ? "es" : ""} with it. You can undo it.`;
        })()}
        confirmLabel="Delete path"
        destructive
        onConfirm={() => {
          const id = deleting?.id;
          setDeleting(null);
          if (id) set((b) => withoutBranch(b, step.id, id));
        }}
      />
    </>
  );
}

function PathCard({
  branch, order, count, nameProblem, fields, catalog, onName, onConditions, onMove, onDelete,
}: {
  branch: WorkflowBranch;
  order: number;
  count: number;
  nameProblem: string | null;
  fields: CatalogConditionField[];
  catalog: WorkflowCatalog | undefined;
  onName: (name: string) => void;
  onConditions: (conditions: Condition[]) => void;
  onMove: (by: -1 | 1) => void;
  onDelete: () => void;
}) {
  const steps = allSteps(branch.steps).length;
  const buttons = React.useRef<HTMLDivElement>(null);
  // The card moves in the list, and a browser drops the focus of an element that moved: put it back
  // on the button pressed, or on the other one once the path reached the top or the bottom.
  const move = (by: -1 | 1) => {
    onMove(by);
    requestAnimationFrame(() => {
      const find = (which: "up" | "down") => buttons.current?.querySelector<HTMLButtonElement>(`[data-move="${which}"]`);
      const pressed = find(by === -1 ? "up" : "down");
      (pressed && !pressed.disabled ? pressed : find(by === -1 ? "down" : "up"))?.focus();
    });
  };
  return (
    <div className="p-3">
      <div className="flex items-center gap-2.5">
        <span
          className="grid size-6 shrink-0 place-items-center rounded-full bg-accent-9 text-xs font-bold text-white dark:text-gray-1"
          aria-hidden
        >
          {order + 1}
        </span>
        <Input
          aria-label={`Name of path ${order + 1}`}
          className="h-9 flex-1 text-sm font-medium"
          maxLength={MAX_BRANCH_NAME}
          value={branch.name}
          invalid={Boolean(nameProblem)}
          onChange={(e) => onName(e.target.value)}
        />
        <div ref={buttons} className="flex shrink-0 items-center">
          <Button data-move="up" variant="ghost" size="icon-sm" aria-label={`Move "${branch.name}" up`} disabled={order === 0} onClick={() => move(-1)}>
            <ArrowUp />
          </Button>
          <Button
            data-move="down"
            variant="ghost"
            size="icon-sm"
            aria-label={`Move "${branch.name}" down`}
            disabled={order === count - 1}
            onClick={() => move(1)}
          >
            <ArrowDown />
          </Button>
          <Button variant="ghost" size="icon-sm" aria-label={`Delete the path "${branch.name}"`} className="text-danger-11" onClick={onDelete}>
            <Trash2 />
          </Button>
        </div>
      </div>
      {nameProblem && (
        <p className="mt-1.5 pl-[34px] text-xs text-danger-11" role="alert">
          {nameProblem}
        </p>
      )}

      <div className="mt-3 space-y-2 pl-[34px]">
        {branch.conditions.length === 0 && (
          <p className="text-xs text-text-muted">No condition yet: add one, or no lead can take this path.</p>
        )}
        {branch.conditions.map((row, i) => (
          // A row has no id of its own. Rows are added at the end and taken out anywhere, so the key
          // also counts them: taking one out redraws the rest, and a value half-typed in one row
          // never shows up in the row that moved into its place.
          <div key={`${i}-${branch.conditions.length}`} className="space-y-2">
            {i > 0 && (
              <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-text-subtle">
                <span className="h-px flex-1 bg-border" aria-hidden />
                and
                <span className="h-px flex-1 bg-border" aria-hidden />
              </p>
            )}
            <ConditionRow
              row={row}
              fields={fields}
              catalog={catalog}
              onChange={(next) => onConditions(branch.conditions.map((r, j) => (j === i ? next : r)))}
              onRemove={() => onConditions(branch.conditions.filter((_, j) => j !== i))}
            />
          </div>
        ))}
        <div className="flex items-center justify-between pt-0.5">
          <Button
            variant="ghost"
            size="sm"
            className="-ml-2 text-accent-11"
            disabled={branch.conditions.length >= MAX_CONDITIONS}
            onClick={() => onConditions([...branch.conditions, { field: "firm", op: "is", values: [] }])}
          >
            <Plus />
            Add condition
          </Button>
          <span className="text-xs text-text-subtle">{steps === 0 ? "No steps yet" : steps === 1 ? "1 step" : `${steps} steps`}</span>
        </div>
      </div>
    </div>
  );
}

function ConditionRow({
  row, fields, catalog, onChange, onRemove,
}: {
  row: Condition;
  fields: CatalogConditionField[];
  catalog: WorkflowCatalog | undefined;
  onChange: (row: Condition) => void;
  onRemove: () => void;
}) {
  const offered = fields.find((f) => f.field === row.field);
  const known = new Set(offered?.values.map((v) => v.value));
  // A firm or a stage the tenant no longer has stays named, with a Remove, until it is taken out:
  // the workflow cannot be published with it. Before the catalog has loaded nothing is called gone.
  const gone = offered?.kind === "pick" ? row.values.filter((v) => !known.has(v)) : [];
  const source = row.field === "source";
  const name = CONDITION_FIELDS[row.field];
  return (
    <div className="space-y-2 rounded-lg border border-border bg-surface-sunken/60 p-3">
      <div className="flex items-center gap-2">
        <Select value={row.field} onValueChange={(v) => onChange({ field: v as ConditionField, op: row.op, values: [] })}>
          <SelectTrigger className="h-9 flex-1 bg-surface text-sm" aria-label="Condition field">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(Object.keys(CONDITION_FIELDS) as ConditionField[]).map((f) => (
              <SelectItem key={f} value={f}>
                {CONDITION_FIELDS[f]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={row.op} onValueChange={(v) => onChange({ ...row, op: v as ConditionOp })}>
          <SelectTrigger className="h-9 flex-1 bg-surface text-sm" aria-label={`How ${name} is checked`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="is">{source ? "starts with" : "is"}</SelectItem>
            <SelectItem value="is_not">{source ? "does not start with" : "is not"}</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="ghost" size="icon-sm" aria-label={`Remove the ${name} condition`} onClick={onRemove}>
          <Trash2 />
        </Button>
      </div>
      {offered?.kind === "text" || (source && !offered) ? (
        <TypedValues name={name} values={row.values} onChange={(values) => onChange({ ...row, values })} />
      ) : (
        <MultiSelect
          label={name}
          placeholder="Choose…"
          className="h-9 w-full max-w-none justify-start"
          options={(offered?.values ?? []).map((v) => ({ value: v.value, label: v.label, keywords: v.value }))}
          value={row.values.filter((v) => known.has(v))}
          // Never cut short here: more than core-api takes is its 422, said on the box.
          onChange={(values) => onChange({ ...row, values: [...gone, ...values] })}
          emptyText="Nothing to pick yet"
          noneText="None picked yet: pick at least one"
        />
      )}
      {offered?.kind === "pick" && row.values.length - gone.length > 1 && (
        // The trigger says "3 selected"; the names are what the admin needs to check.
        <p className="text-xs text-text-muted">{orList(row.values.filter((v) => known.has(v)).map((v) => valueLabel(row.field, v, catalog)))}</p>
      )}
      {gone.map((v) => (
        <div key={v} className="flex items-center justify-between gap-2 rounded-md border border-warning-4 bg-warning-2 px-3 py-1.5 text-xs">
          <span>
            <span className="font-medium text-text">{v}</span>{" "}
            <span className="text-warning-11">is not one of your {row.field === "firm" ? "firms" : "stages"}.</span>
          </span>
          <Button variant="ghost" size="sm" onClick={() => onChange({ ...row, values: row.values.filter((x) => x !== v) })}>
            Remove
          </Button>
        </div>
      ))}
      {offered?.note && <p className="text-xs text-text-muted">{offered.note}</p>}
    </div>
  );
}

/** Values typed rather than picked (a source): Enter or Add puts one in, each shows as a chip, and
 *  leaving the box keeps what was typed, so Done never drops a value the admin did not add. */
function TypedValues({ name, values, onChange }: { name: string; values: string[]; onChange: (values: string[]) => void }) {
  const [draft, setDraft] = React.useState("");
  const add = () => {
    const value = draft.trim().toLowerCase();
    if (value && !values.includes(value) && values.length < MAX_VALUES) onChange([...values, value]);
    setDraft("");
  };
  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Input
          className="h-9 bg-surface text-sm"
          value={draft}
          maxLength={100}
          placeholder="Like website or facebook"
          aria-label={`${name} value`}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={add}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
        />
        <Button variant="outline" className="h-9" disabled={!draft.trim()} onClick={add}>
          Add
        </Button>
      </div>
      {values.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {values.map((v) => (
            <span key={v} className="inline-flex items-center gap-1 rounded-full border border-border bg-surface py-0.5 pl-2.5 pr-1 text-xs text-text">
              {v}
              <button
                type="button"
                aria-label={`Remove ${v}`}
                onClick={() => onChange(values.filter((x) => x !== v))}
                className="grid size-4 place-items-center rounded-full text-text-subtle hover:bg-surface-hover hover:text-text"
              >
                <X className="size-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

const perOf = (unit: Unit) => UNITS.find((u) => u.value === unit)!.seconds;

function WaitFields({ step, set }: { step: Extract<WorkflowStep, { type: "wait" }>; set: SetBody }) {
  const [unit, setUnit] = React.useState<Unit>(() => unitOf(step.seconds));
  const [amount, setAmount] = React.useState(() => String(step.seconds / perOf(unitOf(step.seconds))));
  const per = perOf(unit);

  // An undo, a redo or a quick pick changes the wait from outside the box: show what it is now.
  // What the admin is typing is left alone while it already says the same.
  React.useEffect(() => {
    if (Math.round(Number(amount) * per) !== step.seconds) {
      const u = unitOf(step.seconds);
      setUnit(u);
      setAmount(String(step.seconds / perOf(u)));
    }
    // Only a change of the wait itself, not every keystroke, may rewrite the box.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step.seconds]);

  const n = Number(amount);
  const seconds = Math.round(n * per);
  const problem = !amount.trim() || !Number.isFinite(n)
    ? "Type how long to wait."
    : !Number.isInteger(n)
      ? "A whole number, please."
      : seconds < MIN_WAIT_SECONDS
        ? "At least 1 minute."
        : seconds > MAX_WAIT_SECONDS
          ? "At most 30 days."
          : null;

  const apply = (nextAmount: string, nextUnit: Unit) => {
    const value = Number(nextAmount);
    const s = Math.round(value * perOf(nextUnit));
    if (nextAmount.trim() && Number.isInteger(value) && s >= MIN_WAIT_SECONDS && s <= MAX_WAIT_SECONDS) {
      set((b) => withStepChange(b, step.id, { seconds: s }), `wait-${step.id}`);
    }
  };

  return (
    <>
      <div className="space-y-1.5">
        <label htmlFor="workflow-wait" className="text-xs font-medium text-text">
          Wait for
        </label>
        <div className="flex gap-2">
          <Input
            id="workflow-wait"
            type="number"
            min={1}
            step={1}
            inputMode="numeric"
            className="w-28"
            invalid={Boolean(problem)}
            aria-describedby={problem ? "workflow-wait-error" : undefined}
            value={amount}
            onChange={(e) => {
              setAmount(e.target.value);
              apply(e.target.value, unit);
            }}
          />
          <Select
            value={unit}
            onValueChange={(v) => {
              const next = v as Unit;
              setUnit(next);
              apply(amount, next);
            }}
          >
            <SelectTrigger className="w-36" aria-label="Minutes, hours or days">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {UNITS.map((u) => (
                <SelectItem key={u.value} value={u.value}>
                  {u.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {problem && (
          <p id="workflow-wait-error" role="alert" className="text-xs text-danger-11">
            {problem}
          </p>
        )}
      </div>
      <div className="space-y-1.5">
        <p className="text-xs font-medium text-text-muted">Quick picks</p>
        <div className="flex flex-wrap gap-1.5">
          {QUICK_WAITS.map((q) => (
            <button
              key={q}
              type="button"
              aria-pressed={q === step.seconds}
              onClick={() => {
                const u = unitOf(q);
                setUnit(u);
                setAmount(String(q / perOf(u)));
                set((b) => withStepChange(b, step.id, { seconds: q }));
              }}
              className={cn(
                "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
                q === step.seconds
                  ? "border-accent-8 bg-accent-2 text-accent-11"
                  : "border-border text-text-muted hover:border-border-strong hover:text-text",
              )}
            >
              {waitText(q)}
            </button>
          ))}
        </div>
      </div>
      <Callout tone="info">A text that would land at night waits for the lead's morning (8 am to 9 pm on their clock).</Callout>
    </>
  );
}

/** A day: what a scheduled stage's call starts at, until the admin says otherwise. */
const DEFAULT_CALL_IN = 86400;

/**
 * Update stage (ACL-370): the tenant's working and scheduled stages, as the board's drag offers
 * them. A scheduled stage also takes when the lead is called, counted from when the step runs,
 * since a workflow has no date to pick. A stage the draft names that the tenant no longer offers
 * stays named until it is changed.
 */
function StageFields({ step, catalog, set }: { step: SetStageStep; catalog: WorkflowCatalog | undefined; set: SetBody }) {
  const stages = catalog?.stages ?? [];
  const chosen = stages.find((s) => s.slug === step.stage);
  return (
    <>
      <Field label="Move the lead to" htmlFor="workflow-stage">
        <Select
          value={step.stage ?? ""}
          onValueChange={(slug) => {
            const scheduled = stages.find((s) => s.slug === slug)?.group === "scheduled";
            set((b) =>
              withStepChange(b, step.id, {
                stage: slug,
                call_in_seconds: scheduled ? (step.call_in_seconds ?? DEFAULT_CALL_IN) : null,
              }),
            );
          }}
        >
          <SelectTrigger id="workflow-stage" aria-label="Stage">
            <SelectValue placeholder="Choose a stage" />
          </SelectTrigger>
          <SelectContent>
            {step.stage && !chosen && (
              <SelectItem value={step.stage} disabled>
                {stageName(step.stage, catalog)} (not offered)
              </SelectItem>
            )}
            {stages.map((s) => (
              <SelectItem key={s.slug} value={s.slug}>
                {s.label}
                {s.group === "scheduled" ? " · scheduled" : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
      {chosen?.group === "scheduled" && step.call_in_seconds !== null && (
        <CallInFields stepId={step.id} seconds={step.call_in_seconds} set={set} />
      )}
      <Callout tone="info">
        End stages (Signed, Do not call and the like) are not offered: an outcome puts a lead there. The lead's stage
        history says this workflow moved them, and a lead already in an end stage stays there.
      </Callout>
    </>
  );
}

/** How long after the step a scheduled stage's call is: minutes, hours or days, as a wait is typed. */
function CallInFields({ stepId, seconds, set }: { stepId: string; seconds: number; set: SetBody }) {
  const [unit, setUnit] = React.useState<Unit>(() => unitOf(seconds));
  const [amount, setAmount] = React.useState(() => String(seconds / perOf(unitOf(seconds))));
  React.useEffect(() => {
    if (Math.round(Number(amount) * perOf(unit)) !== seconds) {
      const u = unitOf(seconds);
      setUnit(u);
      setAmount(String(seconds / perOf(u)));
    }
    // Only a change from outside the box (an undo) may rewrite it, not every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seconds]);
  const n = Number(amount);
  const total = Math.round(n * perOf(unit));
  const problem = !amount.trim() || !Number.isFinite(n)
    ? "Type when the lead is called."
    : !Number.isInteger(n)
      ? "A whole number, please."
      : total < MIN_CALL_IN_SECONDS
        ? "At least 1 minute."
        : total > MAX_CALL_IN_SECONDS
          ? `At most ${MAX_CALL_IN_SECONDS / 86400} days.`
          : null;
  const apply = (nextAmount: string, nextUnit: Unit) => {
    const value = Number(nextAmount);
    const s = Math.round(value * perOf(nextUnit));
    if (nextAmount.trim() && Number.isInteger(value) && s >= MIN_CALL_IN_SECONDS && s <= MAX_CALL_IN_SECONDS) {
      set((b) => withStepChange(b, stepId, { call_in_seconds: s }), `call-in-${stepId}`);
    }
  };
  return (
    <div className="space-y-1.5">
      <label htmlFor="workflow-call-in" className="text-xs font-medium text-text">
        Call the lead in
      </label>
      <div className="flex gap-2">
        <Input
          id="workflow-call-in"
          type="number"
          min={1}
          step={1}
          inputMode="numeric"
          className="w-28"
          invalid={Boolean(problem)}
          aria-describedby={problem ? "workflow-call-in-error" : undefined}
          value={amount}
          onChange={(e) => {
            setAmount(e.target.value);
            apply(e.target.value, unit);
          }}
        />
        <Select
          value={unit}
          onValueChange={(v) => {
            const next = v as Unit;
            setUnit(next);
            apply(amount, next);
          }}
        >
          <SelectTrigger className="w-36" aria-label="Minutes, hours or days">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {UNITS.map((u) => (
              <SelectItem key={u.value} value={u.value}>
                {u.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {problem ? (
        <p id="workflow-call-in-error" role="alert" className="text-xs text-danger-11">
          {problem}
        </p>
      ) : (
        <p className="text-xs text-text-muted">The dialer does not call the lead before then.</p>
      )}
    </div>
  );
}

/** A catalog field as the contact page's input wants it, holding the step's value. */
function asRecordField(field: CatalogField, value: FieldStepValue | null): RecordField {
  return {
    key: field.key,
    title: field.title,
    type: field.type,
    required: false,
    sort_order: 0,
    system: false,
    storage: "value",
    config: field.config,
    value,
    access: "edit",
    can_edit: true,
  };
}

/**
 * Update field (ACL-370): one of the lead's own fields, and its value in the input the contact page
 * draws for it, checked the same way (`parseDraft`). Only a value that fits is put in the draft;
 * one that does not is said under the input and the draft keeps no value, which publishing names.
 */
function FieldFields({ step, catalog, set }: { step: SetFieldStep; catalog: WorkflowCatalog | undefined; set: SetBody }) {
  const fields = catalog?.fields ?? [];
  const chosen = fields.find((f) => f.key === step.field);
  const [draft, setDraft] = React.useState<Draft>(() => (chosen ? draftOf(asRecordField(chosen, step.value)) : ""));
  const [problem, setProblem] = React.useState<string | null>(null);
  // Another field, or an undo, gives the input the step's value again.
  React.useEffect(() => {
    if (chosen) setDraft(draftOf(asRecordField(chosen, step.value)));
    setProblem(null);
    // Only a change of field or a value from outside the input may rewrite what is typed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step.field]);
  return (
    <>
      <Field label="Field" htmlFor="workflow-field">
        <Select value={step.field ?? ""} onValueChange={(key) => set((b) => withStepChange(b, step.id, { field: key, value: null }))}>
          <SelectTrigger id="workflow-field" aria-label="Field">
            <SelectValue placeholder="Choose a field" />
          </SelectTrigger>
          <SelectContent>
            {step.field && !chosen && (
              <SelectItem value={step.field} disabled>
                {fieldTitle(step.field, catalog)} (not offered)
              </SelectItem>
            )}
            {fields.map((f) => (
              <SelectItem key={f.key} value={f.key}>
                {f.title}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
      {chosen && (
        <div className="space-y-1.5">
          <FieldControl
            entity="lead"
            field={asRecordField(chosen, step.value)}
            draft={draft}
            problem={problem ?? undefined}
            idPrefix="workflow-field-value"
            onChange={(next) => {
              setDraft(next);
              const parsed = parseDraft(asRecordField(chosen, step.value), next);
              setProblem(parsed.ok ? null : parsed.problem);
              const value = parsed.ok ? (parsed.value as FieldStepValue | null) : null;
              set((b) => withStepChange(b, step.id, { value }), `field-value-${step.id}`);
            }}
            onPick={() => undefined}
          />
        </div>
      )}
      {fields.length === 0 && catalog && (
        <Callout tone="info">No field of the contact can be set by a workflow yet.</Callout>
      )}
      <Callout tone="info">
        The value is checked as when a rep edits the field. Enrollment history says the field was set, never the value.
      </Callout>
    </>
  );
}

/** Add note (ACL-370): the note's words, put on the lead's timeline as from this workflow. */
function NoteFields({ step, set }: { step: NoteStep; set: SetBody }) {
  const text = step.text ?? "";
  return (
    <>
      <Field label="Note" htmlFor="workflow-note" hint={`${text.length.toLocaleString("en-US")} / ${MAX_NOTE_CHARS.toLocaleString("en-US")}`}>
        <Textarea
          id="workflow-note"
          rows={5}
          maxLength={MAX_NOTE_CHARS}
          className="font-sans text-sm"
          placeholder="Moved by the No answer follow-up"
          value={text}
          onChange={(e) => set((b) => withStepChange(b, step.id, { text: e.target.value || null }), `note-${step.id}`)}
        />
      </Field>
      <Callout tone="info">The note goes on the lead's timeline, marked as from this workflow.</Callout>
    </>
  );
}

/** The tenant's active people as a Select's items, with the one a step names kept, marked, when
 *  they are no longer one of them. */
function PersonItems({ chosen, catalog }: { chosen: number | null; catalog: WorkflowCatalog | undefined }) {
  const people = catalog?.people ?? [];
  return (
    <>
      {chosen !== null && catalog && !people.some((p) => p.id === chosen) && (
        <SelectItem value={String(chosen)} disabled>
          Somebody no longer active
        </SelectItem>
      )}
      {people.map((p) => (
        <SelectItem key={p.id} value={String(p.id)}>
          {p.name}
        </SelectItem>
      ))}
    </>
  );
}

/** Assign owner (ACL-408): one of the tenant's active people. */
function OwnerFields({ step, catalog, set }: { step: AssignOwnerStep; catalog: WorkflowCatalog | undefined; set: SetBody }) {
  return (
    <>
      <Field label="Give the lead to" htmlFor="workflow-owner">
        <Select
          value={step.user_id === null ? "" : String(step.user_id)}
          onValueChange={(v) => set((b) => withStepChange(b, step.id, { user_id: Number(v) }))}
        >
          <SelectTrigger id="workflow-owner" aria-label="Owner">
            <SelectValue placeholder="Choose a person" />
          </SelectTrigger>
          <SelectContent>
            <PersonItems chosen={step.user_id} catalog={catalog} />
          </SelectContent>
        </Select>
      </Field>
      <Callout tone="info">
        The owner shows on the lead's record, and its timeline says this workflow gave it to them. It decides nothing else
        yet: who can open the lead and whom the dialer calls them for stay as they are.
      </Callout>
    </>
  );
}

const QUICK_DUES = [0, 15 * 60, 3600, 86400];

/** Create task (ACL-408): a title, when it is due after the step, and for whom. */
function TaskFields({ step, catalog, set }: { step: CreateTaskStep; catalog: WorkflowCatalog | undefined; set: SetBody }) {
  const title = step.title ?? "";
  return (
    <>
      <Field label="Title" htmlFor="workflow-task-title" hint={`${title.length} / ${MAX_TASK_TITLE}`}>
        <Input
          id="workflow-task-title"
          maxLength={MAX_TASK_TITLE}
          placeholder="Call back within 15 minutes"
          value={title}
          onChange={(e) => set((b) => withStepChange(b, step.id, { title: e.target.value || null }), `task-title-${step.id}`)}
        />
      </Field>
      <DueFields stepId={step.id} seconds={step.due_in_seconds} set={set} />
      <fieldset className="space-y-2">
        <legend className="text-xs font-medium text-text">For</legend>
        <div className="space-y-2" role="radiogroup" aria-label="Who the task is for">
          <Choice
            selected={step.assignee === "owner"}
            title="The lead's owner"
            description="Whoever owns the lead when this step runs. A lead nobody owns stops here."
            onSelect={() => set((b) => withStepChange(b, step.id, { assignee: "owner", user_id: null }))}
          />
          <Choice
            selected={step.assignee === "user"}
            title="One person"
            description="Always the same person."
            onSelect={() => set((b) => withStepChange(b, step.id, { assignee: "user" }))}
          />
        </div>
        {step.assignee === "user" && (
          <Select
            value={step.user_id === null ? "" : String(step.user_id)}
            onValueChange={(v) => set((b) => withStepChange(b, step.id, { user_id: Number(v) }))}
          >
            <SelectTrigger aria-label="Person">
              <SelectValue placeholder="Choose a person" />
            </SelectTrigger>
            <SelectContent>
              <PersonItems chosen={step.user_id} catalog={catalog} />
            </SelectContent>
          </Select>
        )}
      </fieldset>
      <Callout tone="info">They see it in Tasks and on the lead, and mark it done there.</Callout>
    </>
  );
}

/** When a task is due: at once, or minutes, hours or days after the step, as a wait is typed. */
function DueFields({ stepId, seconds, set }: { stepId: string; seconds: number | null; set: SetBody }) {
  const [unit, setUnit] = React.useState<Unit>(() => (seconds ? unitOf(seconds) : "minutes"));
  const [amount, setAmount] = React.useState(() => (seconds === null ? "" : String(seconds / perOf(seconds ? unitOf(seconds) : "minutes"))));
  React.useEffect(() => {
    if (seconds === null) {
      if (amount !== "") setAmount("");
      return;
    }
    if (Math.round(Number(amount) * perOf(unit)) !== seconds || amount === "") {
      const u = seconds ? unitOf(seconds) : unit;
      setUnit(u);
      setAmount(String(seconds / perOf(u)));
    }
    // Only a change from outside the box (an undo, a quick pick) may rewrite it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seconds]);
  const n = Number(amount);
  const total = Math.round(n * perOf(unit));
  const problem = !amount.trim()
    ? null
    : !Number.isFinite(n) || n < 0
      ? "Type when the task is due."
      : !Number.isInteger(n)
        ? "A whole number, please."
        : total > MAX_DUE_IN_SECONDS
          ? `At most ${MAX_DUE_IN_SECONDS / 86400} days.`
          : null;
  const apply = (nextAmount: string, nextUnit: Unit) => {
    const value = Number(nextAmount);
    const s = Math.round(value * perOf(nextUnit));
    if (nextAmount.trim() && Number.isInteger(value) && s >= 0 && s <= MAX_DUE_IN_SECONDS) {
      set((b) => withStepChange(b, stepId, { due_in_seconds: s }), `due-${stepId}`);
    }
  };
  return (
    <div className="space-y-1.5">
      <label htmlFor="workflow-task-due" className="text-xs font-medium text-text">
        Due after the step
      </label>
      <div className="flex gap-2">
        <Input
          id="workflow-task-due"
          type="number"
          min={0}
          step={1}
          inputMode="numeric"
          className="w-28"
          invalid={Boolean(problem)}
          aria-describedby={problem ? "workflow-task-due-error" : undefined}
          value={amount}
          onChange={(e) => {
            setAmount(e.target.value);
            apply(e.target.value, unit);
          }}
        />
        <Select
          value={unit}
          onValueChange={(v) => {
            const next = v as Unit;
            setUnit(next);
            apply(amount, next);
          }}
        >
          <SelectTrigger className="w-36" aria-label="Minutes, hours or days">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {UNITS.map((u) => (
              <SelectItem key={u.value} value={u.value}>
                {u.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {problem && (
        <p id="workflow-task-due-error" role="alert" className="text-xs text-danger-11">
          {problem}
        </p>
      )}
      <div className="flex flex-wrap gap-1.5">
        {QUICK_DUES.map((q) => (
          <button
            key={q}
            type="button"
            aria-pressed={q === seconds}
            onClick={() => set((b) => withStepChange(b, stepId, { due_in_seconds: q }))}
            className={cn(
              "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
              q === seconds ? "border-accent-8 bg-accent-2 text-accent-11" : "border-border text-text-muted hover:border-border-strong hover:text-text",
            )}
          >
            {q === 0 ? "At once" : waitText(q)}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Send Slack message (ACL-408): one of the tenant's Slack connections, a channel from its list, and
 * the words, with buttons that put a lead's detail in at the cursor and a preview filled with a
 * sample lead. The channel's name is kept with its id, so the box can say it without asking Slack.
 */
function SlackFields({ step, catalog, set }: { step: SendSlackStep; catalog: WorkflowCatalog | undefined; set: SetBody }) {
  const connections = catalog?.slack_connections ?? [];
  const chosen = connections.find((c) => c.id === step.connection_id);
  const channels = useSlackChannels(step.connection_id, Boolean(chosen?.usable));
  const text = step.text ?? "";
  // Where the cursor was last in the words, so a detail goes in there and not at the end.
  const caret = React.useRef<[number, number] | null>(null);
  const fields = catalog?.merge_fields?.length ? catalog.merge_fields : MERGE_FIELDS;
  const problem = text.trim() ? slackTextProblem(text, catalog) : null;
  const insert = (key: string) => {
    const token = `{{${key}}}`;
    const [start, end] = caret.current ?? [text.length, text.length];
    const next = text.slice(0, start) + token + text.slice(end);
    set((b) => withStepChange(b, step.id, { text: next }), `slack-text-${step.id}`);
    const at = start + token.length;
    caret.current = [at, at];
    requestAnimationFrame(() => {
      const el = document.getElementById("workflow-slack-text") as HTMLTextAreaElement | null;
      el?.focus();
      el?.setSelectionRange(at, at);
    });
  };
  if (catalog && connections.length === 0) {
    return (
      <Callout tone="info">
        No Slack workspace is connected yet. Connect one in{" "}
        <Link to="/slack" className="font-medium underline underline-offset-4">
          Settings → Slack
        </Link>
        , then choose its channel here.
      </Callout>
    );
  }
  return (
    <>
      <Field label="Slack connection" htmlFor="workflow-slack-connection">
        <Select
          value={step.connection_id === null ? "" : String(step.connection_id)}
          onValueChange={(v) =>
            set((b) => withStepChange(b, step.id, { connection_id: Number(v), channel_id: null, channel_name: null }))
          }
        >
          <SelectTrigger id="workflow-slack-connection" aria-label="Slack connection">
            <SelectValue placeholder="Choose a connection" />
          </SelectTrigger>
          <SelectContent>
            {step.connection_id !== null && catalog && !chosen && (
              <SelectItem value={String(step.connection_id)} disabled>
                A connection you no longer have
              </SelectItem>
            )}
            {connections.map((c) => (
              <SelectItem key={c.id} value={String(c.id)}>
                {c.name}
                {c.team_name ? ` · ${c.team_name}` : ""}
                {c.usable ? "" : " (needs attention)"}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
      {chosen && !chosen.usable && (
        <Callout tone="warning">
          This connection is turned off, or Slack refused its token. Fix it in{" "}
          <Link to="/slack" className="font-medium underline underline-offset-4">
            Settings → Slack
          </Link>
          , or choose another.
        </Callout>
      )}
      {chosen?.usable && (
        <Field label="Channel" htmlFor="workflow-slack-channel" hint="Invite the app to a private channel first.">
          <Select
            value={step.channel_id ?? ""}
            onValueChange={(id) => {
              const picked = channels.data?.find((c) => c.id === id);
              set((b) => withStepChange(b, step.id, { channel_id: id, channel_name: picked?.name ?? null }));
            }}
          >
            <SelectTrigger id="workflow-slack-channel" aria-label="Channel">
              <SelectValue placeholder={channels.isFetching ? "Loading channels…" : "Choose a channel"} />
            </SelectTrigger>
            <SelectContent>
              {step.channel_id && !channels.data?.some((c) => c.id === step.channel_id) && (
                <SelectItem value={step.channel_id} disabled>
                  #{step.channel_name ?? step.channel_id}
                </SelectItem>
              )}
              {channels.data?.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  #{c.name}
                  {c.is_private ? " (private)" : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      )}
      {channels.error && (
        <Callout tone="danger">
          {channels.error.message}{" "}
          <Button type="button" variant="ghost" size="sm" onClick={() => void channels.refetch()}>
            Try again
          </Button>
        </Callout>
      )}
      <div className="space-y-1.5">
        <Field
          label="Message"
          htmlFor="workflow-slack-text"
          hint={`${text.length.toLocaleString("en-US")} / ${MAX_SLACK_CHARS.toLocaleString("en-US")}`}
          error={problem ?? undefined}
        >
          <Textarea
            id="workflow-slack-text"
            rows={4}
            maxLength={MAX_SLACK_CHARS}
            className="font-sans text-sm"
            placeholder="{{first_name}} {{last_name}} replied. Call them back."
            value={text}
            invalid={Boolean(problem)}
            onChange={(e) => set((b) => withStepChange(b, step.id, { text: e.target.value || null }), `slack-text-${step.id}`)}
            onSelect={(e) => {
              caret.current = [e.currentTarget.selectionStart, e.currentTarget.selectionEnd];
            }}
          />
        </Field>
        <div className="flex flex-wrap gap-1.5">
          {fields.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => insert(f.key)}
              className="rounded-full border border-border px-2.5 py-0.5 text-xs text-text-muted transition-colors hover:border-border-strong hover:text-text"
            >
              + {f.label}
            </button>
          ))}
        </div>
      </div>
      {text.trim() && !problem && (
        <div className="space-y-1">
          <p className="text-xs font-medium text-text-muted">Preview, for a sample lead</p>
          <p className="whitespace-pre-wrap rounded-md border border-border bg-surface-sunken px-3 py-2 text-sm text-text">
            {fillText(text, sampleValues(fields))}
          </p>
        </div>
      )}
      <Callout tone="info">
        A lead's details go in as written and can't mention or link anyone. If Slack can't take the message, Enrollment
        history says why, and the lead goes on to the next step.
      </Callout>
    </>
  );
}

function SmsFields({
  step, catalog, set,
}: {
  step: Extract<WorkflowStep, { type: "send_text" }>;
  catalog: WorkflowCatalog | undefined;
  set: SetBody;
}) {
  // A template that is off is not offered, but the one this step already sends stays named. An
  // email template is never a text's.
  const templates = (catalog?.templates ?? []).filter(
    (t) => t.channel !== "email" && (t.active !== false || t.slug === step.template),
  );
  const chosen = templates.find((t) => t.slug === step.template);
  if (!stepAvailable(catalog, "send_text")) {
    return (
      <Callout tone="info">
        Coming soon: text templates are not here yet. This step can stay in the draft, but the workflow can't be
        published with it until they arrive.
      </Callout>
    );
  }
  if (templates.length === 0) {
    return (
      <Callout tone="info">
        You have no text templates yet. Write one in{" "}
        <Link to="/text-templates/new" className="font-medium underline underline-offset-4">
          Settings → Automation → Text templates
        </Link>
        , then pick it here.
      </Callout>
    );
  }
  return (
    <>
      <Field label="Template" htmlFor="workflow-template">
        <Select value={step.template ?? ""} onValueChange={(template) => set((b) => withStepChange(b, step.id, { template }))}>
          <SelectTrigger id="workflow-template" aria-label="Template">
            <SelectValue placeholder="Choose a template" />
          </SelectTrigger>
          <SelectContent>
            {templates.map((t) => (
              <SelectItem key={t.slug} value={t.slug}>
                {t.name} · {t.languages.map((l) => LANGUAGE_NAMES[l] ?? l).join(" and ")}
                {t.active === false ? " (turned off)" : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
      {chosen?.active === false ? (
        <Callout tone="warning">
          This template is turned off, so the workflow can't be published with it. Turn it on in Settings → Automation →
          Text templates, or choose another.
        </Callout>
      ) : chosen ? (
        <Callout tone="info">
          {chosen.languages.length > 1
            ? "Each lead gets it in their own language, English or Spanish."
            : `Every lead gets it in ${LANGUAGE_NAMES[chosen.languages[0] ?? "en"] ?? "English"}.`}{" "}
          Do-not-call and STOP are checked before it goes. Outside 8 am to 9 pm on the lead's clock, it waits
          for their morning.
        </Callout>
      ) : null}
    </>
  );
}

/** Send email (ACL-378): one of the tenant's email templates, as Send SMS picks a text one. */
function EmailFields({
  step, catalog, set,
}: {
  step: Extract<WorkflowStep, { type: "send_email" }>;
  catalog: WorkflowCatalog | undefined;
  set: SetBody;
}) {
  // An email template that is off is not offered, but the one this step already sends stays named.
  const templates = (catalog?.templates ?? []).filter(
    (t) => t.channel === "email" && (t.active !== false || t.slug === step.template),
  );
  const chosen = templates.find((t) => t.slug === step.template);
  if (!stepAvailable(catalog, "send_email")) {
    return (
      <Callout tone="info">
        Coming soon: workflows do not send email yet. This step can stay in the draft, but the workflow can't be
        published with it until they do.
      </Callout>
    );
  }
  if (templates.length === 0) {
    return (
      <Callout tone="info">
        You have no email templates yet. Write one in{" "}
        <Link to="/email-templates/new" className="font-medium underline underline-offset-4">
          Settings → Automation → Email templates
        </Link>
        , then pick it here.
      </Callout>
    );
  }
  return (
    <>
      <Field label="Email template" htmlFor="workflow-email-template">
        <Select value={step.template ?? ""} onValueChange={(template) => set((b) => withStepChange(b, step.id, { template }))}>
          <SelectTrigger id="workflow-email-template" aria-label="Email template">
            <SelectValue placeholder="Choose an email template" />
          </SelectTrigger>
          <SelectContent>
            {templates.map((t) => (
              <SelectItem key={t.slug} value={t.slug}>
                {t.name} · {t.languages.map((l) => LANGUAGE_NAMES[l] ?? l).join(" and ")}
                {t.active === false ? " (turned off)" : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
      {chosen?.active === false ? (
        <Callout tone="warning">
          This email template is turned off, so the workflow can't be published with it. Turn it on in Settings →
          Automation → Email templates, or choose another.
        </Callout>
      ) : chosen ? (
        <Callout tone="info">
          {chosen.languages.length > 1
            ? "Each lead gets it in their own language, English or Spanish."
            : `Every lead gets it in ${LANGUAGE_NAMES[chosen.languages[0] ?? "en"] ?? "English"}.`}{" "}
          It goes to the email address on their record, into their email thread, and ends with "Don't want these
          emails? Reply STOP." A lead who replied STOP is skipped. Outside 8 am to 9 pm on the lead's clock, it waits
          for their morning.
        </Callout>
      ) : null}
    </>
  );
}

function Choice({
  selected, title, description, onSelect, disabled = false, soon = false,
}: {
  selected: boolean;
  title: string;
  description: string;
  onSelect: () => void;
  disabled?: boolean;
  /** Draws the "Coming soon" tag the action list draws, for a choice the server cannot run yet. */
  soon?: boolean;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      disabled={disabled}
      onClick={onSelect}
      className={cn(
        "flex w-full items-start gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-8/40",
        "disabled:cursor-not-allowed disabled:opacity-60",
        selected ? "border-accent-8 bg-accent-2" : "border-border bg-surface hover:border-border-strong hover:bg-surface-hover",
      )}
    >
      <span
        className={cn(
          "mt-0.5 grid size-4 shrink-0 place-items-center rounded-full border",
          selected ? "border-accent-9 bg-accent-9" : "border-border-strong",
        )}
        aria-hidden
      >
        {selected && <span className="size-1.5 rounded-full bg-white dark:bg-gray-1" />}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium text-text">{title}</span>
        <span className="block text-xs leading-5 text-text-muted">{description}</span>
      </span>
      {soon && <SoonTag />}
    </button>
  );
}

function SoonTag() {
  return (
    <span className="shrink-0 rounded-full border border-border px-2 py-0.5 text-[11px] font-medium text-text-muted">
      Coming soon
    </span>
  );
}

// --- the Settings tab ------------------------------------------------------------------------

/** The workflow's own settings: whether a reply stops it, and how late a text may be. */
export function WorkflowSettingsCard({ body, set, readOnly }: { body: WorkflowBody; set: SetBody; readOnly: boolean }) {
  const { stop_on_reply, max_late_minutes } = body.settings;
  return (
    <Card className="mx-auto max-w-2xl divide-y divide-border">
      <div className="flex items-start justify-between gap-4 px-5 py-4">
        <div>
          <p id="stop-on-reply" className="text-sm font-medium text-text">
            Stop when the lead replies
          </p>
          <p className="text-xs text-text-muted">A lead who texts back gets no more texts from this workflow: a rep answers them instead.</p>
        </div>
        <Switch
          checked={stop_on_reply}
          disabled={readOnly}
          aria-labelledby="stop-on-reply"
          onCheckedChange={(on) => set((b) => ({ ...b, settings: { ...b.settings, stop_on_reply: on } }))}
        />
      </div>
      <div className="flex flex-wrap items-start justify-between gap-4 px-5 py-4">
        <div className="min-w-0 flex-1">
          <p id="max-late" className="text-sm font-medium text-text">
            Skip a text that is this late
          </p>
          <p className="text-xs text-text-muted">
            If texts are held up, by a pause or an outage, a text later than this is not sent, so nobody gets "Free for a quick
            call?" a day late.
          </p>
        </div>
        <Select
          value={String(max_late_minutes)}
          disabled={readOnly}
          onValueChange={(v) => set((b) => ({ ...b, settings: { ...b.settings, max_late_minutes: Number(v) } }))}
        >
          <SelectTrigger aria-labelledby="max-late" className="w-36">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {[...new Set([...LATE_CHOICES, max_late_minutes])]
              .sort((a, b) => a - b)
              .map((m) => (
                <SelectItem key={m} value={String(m)}>
                  {waitText(m * 60)}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
      </div>
    </Card>
  );
}
